// The web app's words in Hindi, key for key with en.ts. Each entry is `draft(…)`, machine-drafted, until
// a person who reads Hindi reviews it and marks it `reviewed(…)`; `pnpm i18n:report` lists the drafts.
import { draft, type Catalogue } from "./catalogue.js";
import type { OnboardingQuestion } from "../api.js";
import { dayIn, quantityIn, type ShortLine, type StopParameters, type Words } from "./en.js";

const shortLine = (l: ShortLine) => {
  const pr = l.requisition;
  const raised = pr
    ? `; ${quantityIn(pr.quantity, l.unit)} के लिए परचेज़ रिक्विज़िशन ${pr.number} बनाई गई${pr.supplier ? `, ${pr.supplier} को` : ""}`
    : "";
  return (
    `${l.name}: ${quantityIn(l.needs, l.unit)} चाहिए, ${quantityIn(l.on_hand, l.unit)} स्टॉक में है, ` +
    `${quantityIn(l.short, l.unit)} कम है${raised}`
  );
};

/** Value Stream names the packs leave in English in Hindi too, as the Floor shows them (#1016). */
const STREAMS_HI: Readonly<Record<string, string>> = {
  "NPD / APQP": "नए पार्ट का विकास (NPD / APQP)",
};

/** The packs' KPI names in Hindi (#1016); one not here is shown as the pack names it. */
const KPIS_HI: Readonly<Record<string, string>> = {
  "GST notices": "GST नोटिस",
  "MSME 45-day exposure": "MSME 45-दिन की देनदारी",
  "job work past its statutory return date": "क़ानूनी वापसी तारीख़ पार कर चुका जॉब वर्क",
  "quote-to-order %": "कोटेशन से ऑर्डर %",
  "quote turnaround hours": "कोटेशन भेजने में लगे घंटे",
  "on-time delivery %": "समय पर डिलीवरी %",
  OEE: "OEE (मशीनों की कुल प्रभावशीलता)",
  "actual vs estimated margin per order": "हर ऑर्डर पर असली बनाम अनुमानित मार्जिन",
  "material cost %": "माल की लागत %",
  "scrap %": "स्क्रैप %",
  "rework %": "रीवर्क %",
  DSO: "DSO (भुगतान आने के औसत दिन)",
  "overdue %": "बकाया %",
  "advance collection %": "एडवांस वसूली %",
};

/** The records Insights reads, as its reasons name them, in Hindi. */
const INSIGHT_RECORDS_HI: Readonly<Record<string, string>> = {
  invoices: "इनवॉइस",
  "payment receipts": "भुगतान रसीदें",
  "sales orders": "सेल्स ऑर्डर",
  "proforma invoices": "प्रोफ़ॉर्मा इनवॉइस",
  enquiries: "पूछताछ",
  quotations: "कोटेशन",
  estimates: "एस्टिमेट",
  "work orders": "वर्क ऑर्डर",
  "job cards": "जॉब कार्ड",
  "stock issues": "स्टॉक निकासी",
  Items: "आइटम",
  "improvement ideas": "सुधार के सुझाव",
};
const recordsHi = (plural: string) => INSIGHT_RECORDS_HI[plural] ?? plural;

/** Why a KPI is not measured, or has no value, in Hindi: the API's fixed reasons, and those naming records (#1016). */
const REASONS_HI: Readonly<Record<string, string>> = {
  "no computation registered": "इसकी गणना LAMP में अभी नहीं बनी है",
  "no data in the months asked": "चुने गए महीनों में कोई आँकड़ा नहीं",
  "no invoices in the months asked": "चुने गए महीनों में कोई इनवॉइस नहीं",
  "no open receivables in the months asked": "चुने गए महीनों में कोई बकाया वसूली नहीं",
  "no orders booked with an advance due in the months asked": "चुने गए महीनों में एडवांस वाला कोई ऑर्डर नहीं",
  "no enquiries won or lost in the months asked": "चुने गए महीनों में कोई पूछताछ जीती या हारी नहीं गई",
  "no quotations first sent in the months asked": "चुने गए महीनों में कोई कोटेशन पहली बार नहीं भेजा गया",
  "no job-card bookings in the months asked": "चुने गए महीनों में जॉब कार्ड पर कोई बुकिंग नहीं",
  "no orders whose work orders all closed in the months asked":
    "चुने गए महीनों में ऐसा कोई ऑर्डर नहीं जिसके सारे वर्क ऑर्डर बंद हुए हों",
  "no material issued to work orders against invoiced sales in the months asked":
    "चुने गए महीनों में इनवॉइस हुई बिक्री के वर्क ऑर्डरों को कोई माल नहीं दिया गया",
  "no improvement ideas closed in the months asked": "चुने गए महीनों में कोई सुधार का सुझाव बंद नहीं हुआ",
  "needs each dispatch recorded in LAMP, to compare with the order's committed date":
    "हर डिस्पैच LAMP में दर्ज होना चाहिए, ताकि ऑर्डर की वादा की गई तारीख़ से मिलाया जा सके",
  "needs each work centre's planned production time recorded in LAMP":
    "हर वर्क सेंटर का योजना वाला उत्पादन समय LAMP में दर्ज होना चाहिए",
  "1 enquiry came in during the months asked, and it is not won or lost yet":
    "चुने गए महीनों में 1 पूछताछ आई, जो अभी जीती या हारी नहीं गई",
};
const REASON_PATTERNS_HI: readonly [RegExp, (...said: string[]) => string][] = [
  [
    /^needs your business to record its (.+) in LAMP, which it does not do yet$/u,
    (records) => `आपके कारोबार को अपने ${recordsHi(records)} LAMP में दर्ज करने होंगे, जो अभी नहीं होते`,
  ],
  [
    /^needs your business to record its (.+) in LAMP$/u,
    (records) => `आपके कारोबार को अपने ${recordsHi(records)} LAMP में दर्ज करने होंगे`,
  ],
  [
    /^needs (.+), which your Roles may not read$/u,
    (records) => `${recordsHi(records)} चाहिए, जो आपके रोल पढ़ नहीं सकते`,
  ],
  [
    /^needs every one of the (\d+) or more (.+) on record, and one read returns at most \d+$/u,
    (limit, records) =>
      `रिकॉर्ड के सभी ${limit} या ज़्यादा ${recordsHi(records ?? "")} चाहिए, और एक बार में ज़्यादा से ज़्यादा ${limit} पढ़े जाते हैं`,
  ],
  [
    /^(\d+) enquiries came in during the months asked, and none is won or lost yet$/u,
    (n) => `चुने गए महीनों में ${n} पूछताछ आईं, जिनमें से कोई अभी जीती या हारी नहीं गई`,
  ],
];
function reasonHi(reason: string): string {
  const fixed = REASONS_HI[reason];
  if (fixed !== undefined) return fixed;
  for (const [pattern, said] of REASON_PATTERNS_HI) {
    const found = pattern.exec(reason);
    if (found) return said(...found.slice(1).map((s) => s ?? ""));
  }
  return reason.charAt(0).toUpperCase() + reason.slice(1);
}

/** Each kind of work LAMP does, by its Task Class, in Hindi (#1016); one not here is in the agents module's words. */
const TASK_CLASSES_HI: Readonly<Record<string, string>> = {
  intent: "संदेश समझना",
  extract_order_fields: "संदेशों से ऑर्डर पढ़ना",
  extract_complaint_fields: "संदेशों से शिकायतें पढ़ना",
  extract_breakdown_fields: "ब्रेकडाउन रिपोर्ट पढ़ना",
  extract_incident_fields: "घटना रिपोर्ट पढ़ना",
  extract_master_data: "संदेशों से ग्राहक, सप्लायर और आइटम पढ़ना",
  extract_enquiry_fields: "संदेशों से पूछताछ पढ़ना",
  extract_purchase_fields: "संदेशों से ख़रीद और माल की प्राप्ति पढ़ना",
  draft_rule_change: "नियमों के बदलाव का ड्राफ़्ट बनाना",
  read_document: "दस्तावेज़ और फ़ोटो पढ़ना",
  recognise_file: "भेजी गई फ़ाइलें पहचानना",
  bom_routing_draft: "ड्रॉइंग से BOM और रूटिंग का ड्राफ़्ट बनाना",
  learn_from_document: "पुराने दस्तावेज़ों से सीखना",
  propose_pack_change: "जूरिस्डिक्शन पैक में बदलाव सुझाना",
  apply_playbook: "प्लेबुक लागू करना",
  credit_decision: "क्रेडिट पर सलाह देना",
  courtesy_reminder: "विनम्र याद-दिहानी लिखना",
  support_answer: "सहायता के सवालों के जवाब देना",
  quotation_cover_note: "कोटेशन के साथ भेजा जाने वाला नोट लिखना",
};

/** Each Nature of Payment in Hindi, by the id the India pack lists it under; any other in the API's words. */
const NATURE_HI: Readonly<Record<string, string>> = {
  contract_work: "ठेके का काम",
  professional_fees: "पेशेवर फ़ीस",
  technical_fees: "तकनीकी फ़ीस",
  rent_land_building: "ज़मीन या भवन का किराया",
  rent_plant_machinery: "प्लांट या मशीनरी का किराया",
  commission: "कमीशन",
  purchase_of_goods: "माल की ख़रीद",
};

/** Hindi for the units the API names; any other stays as the API says it. */
const unitHi = (unit: string, one: boolean) =>
  unit === "days" ? "दिन" : unit === "hours" ? (one ? "घंटा" : "घंटे") : unit === "orders" ? "ऑर्डर" : unit;

/** A Business Profile value onboarding sets, in plain words, by profile key and value (en.ts's PROFILE_WORDS). */
const PROFILE_WORDS: Readonly<Record<string, Readonly<Record<string, string>>>> = {
  manufacturing_mode: {
    machining: "ग्राहक की ड्रॉइंग से पार्ट्स की मशीनिंग",
    fabrication: "ऑर्डर पर शीट-मेटल या स्ट्रक्चरल फ़ैब्रिकेशन",
    assembly: "ऑर्डर पर मशीनें या पैनल असेंबल करना",
    moulding: "ऑर्डर पर मोल्डिंग",
    casting: "ऑर्डर पर कास्टिंग या फ़ोर्जिंग",
    "batch-process": "बैच में बनाना",
  },
  gst_registration: {
    regular: "GST में रजिस्टर्ड",
    composition: "GST कंपोज़िशन स्कीम में रजिस्टर्ड",
    unregistered: "GST में रजिस्टर्ड नहीं",
  },
  size_band: { micro: "सूक्ष्म उद्यम", small: "लघु उद्यम", medium: "मध्यम उद्यम" },
  headcount_band: { "<10": "10 से कम लोग", "10–19": "10 से 19 लोग", "20+": "20 या ज़्यादा लोग" },
  software: {
    "named platform": "हिसाब अकाउंटिंग या ERP सॉफ़्टवेयर में",
    spreadsheets: "हिसाब स्प्रेडशीट में",
    none: "हिसाब कागज़ पर",
  },
};

/** Set up LAMP's questions, by question id: in Hindi from the first question on (#465). */
const ONBOARDING_PROMPTS: Readonly<
  Record<string, (gstin: string | undefined, question: OnboardingQuestion) => string>
> = {
  // Telegram is named only where the Cell offers it (#512).
  channel: (_gstin, question) =>
    question.options.some((o) => o.id === "telegram")
      ? "आप LAMP का इस्तेमाल कहाँ करना चाहेंगे? शॉप फ़्लोर पर Telegram सबसे अच्छा चलता है।"
      : "आप LAMP का इस्तेमाल कहाँ करना चाहेंगे?",
  industry: () => "आपका कारोबार क्या करता है?",
  registration: () => "आपका GSTIN क्या है? अगर आप रजिस्टर्ड नहीं हैं, तो इसे खाली छोड़ दें।",
  registration_details: (gstin) =>
    `हम ${gstin ?? "आपका GSTIN"} नहीं खोज पाए। रजिस्टर्ड कारोबार का नाम क्या है, और रजिस्ट्रेशन रेगुलर है या कंपोज़िशन?`,
  price: () =>
    "LAMP अभी आपके जैसे कारोबार के लिए तैयार नहीं है। तैयार होते ही हम आपको बताएँगे। आप इसके लिए कितना देना चाहेंगे?",
  particulars: () =>
    "आपके इनवॉइस पर आपके कारोबार का पता और उन पर हस्ताक्षर करने वाले का नाम छपता है। रजिस्टर्ड पता क्या है, वह किस राज्य में है, और अधिकृत हस्ताक्षरकर्ता का नाम क्या है?",
  turnover: () => "पिछले साल आपका टर्नओवर कितना था?",
  headcount: () => "आपके कारोबार में कितने लोग काम करते हैं?",
  priorities: () => "अभी आपके लिए सबसे ज़रूरी क्या है? 2 या 3 चुनें, सबसे ज़रूरी पहले।",
  tools: () => "आज आप किसका इस्तेमाल करते हैं? जो भी लागू हों, सब चुनें।",
};

/** The choices of Set up LAMP's questions, by question and choice id. The priorities come from the pack's i18n. */
const ONBOARDING_OPTIONS: Readonly<Record<string, Readonly<Record<string, string>>>> = {
  channel: { telegram: "Telegram", web: "वेब चैट" },
  industry: {
    machining: "ग्राहक की ड्रॉइंग से पार्ट्स की मशीनिंग",
    fabrication: "ऑर्डर पर शीट-मेटल या स्ट्रक्चरल फ़ैब्रिकेशन",
    assembly: "ऑर्डर पर मशीनें या पैनल असेंबल करना",
    moulding: "ऑर्डर पर मोल्डिंग",
    casting: "ऑर्डर पर कास्टिंग या फ़ोर्जिंग",
    villas: "विला या हॉलिडे होम का प्रबंधन",
    trading: "ट्रेडिंग या डिस्ट्रीब्यूशन",
    retail: "दुकान",
    food: "रेस्टोरेंट, बेकरी या खाने का कारोबार",
    services: "सेवाएँ",
    other: "कुछ और",
  },
  registration_details: { regular: "रेगुलर", composition: "कंपोज़िशन" },
  turnover: {
    "under-1cr": "₹1 करोड़ से कम",
    "1-5cr": "₹1 करोड़ से ₹5 करोड़",
    "5-25cr": "₹5 करोड़ से ₹25 करोड़",
    "25-100cr": "₹25 करोड़ से ₹100 करोड़",
    "over-100cr": "₹100 करोड़ से ज़्यादा",
  },
  headcount: { "<10": "10 से कम लोग", "10–19": "10 से 19 लोग", "20+": "20 या ज़्यादा लोग" },
  tools: {
    tally: "Tally",
    busy: "Busy",
    zoho: "Zoho Books",
    "other-software": "कोई और अकाउंटिंग या ERP सॉफ़्टवेयर",
    spreadsheets: "Excel या Google Sheets",
    paper: "कागज़ के रजिस्टर",
  },
  price: {
    "under-1000": "₹1,000 महीने से कम",
    "1000-3000": "₹1,000 से ₹3,000 महीना",
    "3000-5000": "₹3,000 से ₹5,000 महीना",
    "over-5000": "₹5,000 महीने से ज़्यादा",
  },
};

/** What the Audit Log records that is not a Run's own event, by the event's name (en.ts's AUDIT_ACTIONS). */
const AUDIT_ACTIONS: Readonly<Record<string, string>> = {
  TreeChangeApplied: "बिज़नेस के काम करने का तरीका बदला",
  FileReceived: "एक फ़ाइल मिली",
  FileKindProposed: "पढ़ा कि फ़ाइल किस तरह का कागज़ है",
  FileKindConfirmed: "पक्का किया कि फ़ाइल किस तरह का कागज़ है",
  FileNotRead: "एक फ़ाइल पढ़ी नहीं जा सकी",
  SupplierParticularsChanged: "इनवॉइस पर बिज़नेस का ब्योरा बदला",
  LanguageChanged: "LAMP की भाषा बदली",
  LicenceStateChanged: "सेल का लाइसेंस बदला",
  ReadOnlyReasonSet: "बिज़नेस को सिर्फ़-पढ़ने वाले मोड में डाला",
  ReadOnlyReasonCleared: "बिज़नेस को सिर्फ़-पढ़ने वाले मोड से निकाला",
  ReleaseSet: "बिज़नेस को LAMP के नए रिलीज़ पर ले गए",
  ReleaseNotesSent: "मालिकों को बताया कि LAMP में क्या बदला",
  ReleaseAlarmFired: "गड़बड़ वाला LAMP रिलीज़ रोका",
  RingChanged: "बदला कि बिज़नेस को LAMP के बदलाव पहले मिलें या नहीं",
  RolloutRolledBack: "LAMP का एक रिलीज़ वापस लिया",
  ContentReleaseImported: "LAMP का नया कंटेंट लिया",
  ContentReleaseRefused: "LAMP का एक कंटेंट नहीं लिया",
  ContentReleaseRolledBack: "LAMP के पिछले कंटेंट पर लौटे",
  ExternalAssistantGranted: "एक बाहरी असिस्टेंट को जुड़ने दिया",
  ExternalAssistantRevoked: "एक बाहरी असिस्टेंट को हटाया",
  ExternalAssistantsRevokedForTenant: "सभी बाहरी असिस्टेंट हटाए",
  ExternalAssistantRefreshTokenReused: "दोबारा इस्तेमाल हुए साइन-इन वाले बाहरी असिस्टेंट को रोका",
  ExternalAssistantToolCalled: "एक बाहरी असिस्टेंट ने LAMP में काम किया",
  BreakGlassOpened: "LAMP सपोर्ट ने बिज़नेस में पहुँच खोली",
  BreakGlassUsed: "LAMP सपोर्ट ने बिज़नेस में अपनी पहुँच इस्तेमाल की",
  BreakGlassClosed: "LAMP सपोर्ट की पहुँच बंद हुई",
  SignedOutEverywhere: "बाकी हर जगह से साइन आउट किया",
  AutonomyLowered: "LAMP अपने-आप कितना करे, यह कम किया",
  KillSwitchEngaged: "LAMP के एजेंट रोके",
  KillSwitchReleased: "LAMP के एजेंट फिर चालू किए",
  ConsentGiven: "अपनी सहमति दी",
  ConsentWithdrawn: "अपनी सहमति वापस ली",
  RightsRequestReceived: "अपने डेटा के बारे में पूछा (गोपनीयता अनुरोध)",
  RightsRequestVerified: "कोड से गोपनीयता अनुरोध पक्का किया",
  RightsRequestCompleted: "गोपनीयता अनुरोध का जवाब दिया",
  RightsRequestRefused: "कारण बताकर गोपनीयता अनुरोध मना किया",
  BusinessDataExported: "बिज़नेस का पूरा डेटा डाउनलोड किया",
  OffboardingStarted: "बिज़नेस का LAMP खाता बंद करना शुरू किया",
  OwnerConfirmed: "पक्का किया कि खाता बंद करने से क्या होता है",
  ArchiveChosen: "चुना कि LAMP संग्रह रखे या नहीं",
  ExportTaken: "बंद करने से पहले बिज़नेस का डेटा लिया",
  OffboardingCancelled: "बिज़नेस का LAMP खाता बंद करना रोका",
  TenantShredded: "बिज़नेस का LAMP खाता हमेशा के लिए बंद किया",
  ScheduledRunSkipped: "समय पर होने वाला एक रन छोड़ा",
  SetupProblemRaised: "बिज़नेस के सेटअप में एक दिक्कत मिली",
  Talk: "LAMP से बात की",
};

const openRuns = (n: number) => `${n} ${n === 1 ? "खुला" : "खुले"} रन`;

export const hi: Catalogue<Words> = {
  // The app as a whole.
  loading: draft("लोड हो रहा है…"),
  languageSwitch: draft("हिंदी / English"),

  // The pages, as the sidebar, the top bar and the tab bar name them.
  pageToday: draft("आज"),
  pageChat: draft("LAMP से बात करें"),
  pageFloor: draft("फ़्लोर"),
  pageRuns: draft("रन"),
  pageDocuments: draft("दस्तावेज़"),
  pageParties: draft("ग्राहक और सप्लायर"),
  pageItems: draft("आइटम और स्टॉक"),
  pageCash: draft("नकद और खाते"),
  pageGst: draft("GST और अनुपालन"),
  pageTree: draft("बिज़नेस ट्री"),
  pageRules: draft("नियम और प्लेबुक"),
  pageTemplates: draft("टेम्पलेट"),
  pageSchedules: draft("शेड्यूल"),
  pageMemory: draft("मेमोरी"),
  pageQuality: draft("गुणवत्ता"),
  pageInsights: draft("इनसाइट्स"),
  pageBills: draft("खर्च के बिल"),
  pageExpenses: draft("खर्च"),
  pageCredits: draft("क्रेडिट और उपयोग"),
  pageAudit: draft("ऑडिट लॉग"),
  pageOnboarding: draft("LAMP सेट अप करें"),
  pageUsers: draft("यूज़र"),
  pageRoles: draft("भूमिकाएँ और अनुमतियाँ"),
  pageChannels: draft("चैनल"),
  pageConnections: draft("कनेक्शन"),
  pageModels: draft("AI मॉडल"),
  pageSettings: draft("सेटिंग्स"),
  pageBilling: draft("प्लान और बिलिंग"),
  pageSecurity: draft("सुरक्षा"),
  pageHelp: draft("मदद"),
  pageStatus: draft("सिस्टम स्थिति"),
  pageRefused: draft((page: string) => `आपकी भूमिकाएँ ${page} नहीं खोलतीं, इसलिए यह आज है।`),
  navRun: draft("कारोबार चलाएँ"),
  navMoney: draft("पैसा और अनुपालन"),
  navHow: draft("LAMP आपके लिए कैसे काम करता है"),
  navWatch: draft("निगरानी"),
  navSetup: draft("सेटअप"),
  tabToday: draft("आज"),
  tabFloor: draft("फ़्लोर"),
  tabChat: draft("चैट"),
  tabDocuments: draft("दस्तावेज़"),

  // The shell.
  shellTitle: draft((page) => `${page} · LAMP`),
  shellSkip: draft("सामग्री पर जाएँ"),
  shellBusiness: draft("आपका बिज़नेस"),
  shellSwitchBusiness: draft("बिज़नेस बदलें"),
  shellOtherBusiness: draft("दूसरे बिज़नेस में साइन इन करें"),
  shellCredits: draft("इस महीने के क्रेडिट"),
  shellTrialLeft: draft((days) => (days === 1 ? "ट्रायल: आखिरी दिन" : `ट्रायल: ${days} दिन बाकी`)),
  shellSearch: draft("खोजें"),
  shellSearchHint: draft("ऑर्डर, ग्राहक, दस्तावेज़ खोजें"),
  shellSearching: draft("खोज रहे हैं…"),
  shellSearchResults: draft("खोज के नतीजे"),
  shellNoMatches: draft("कुछ नहीं मिला।"),
  shellSearchFailed: draft("खोज नहीं हो सकी। थोड़ी देर में फिर कोशिश करें।"),
  shellOrder: draft("ऑर्डर"),
  shellCounterparty: draft("ग्राहक या सप्लायर"),
  shellDocument: draft("दस्तावेज़"),
  shellNotifications: draft("सूचनाएँ"),
  shellEmailMe: draft("मुझे ईमेल करें"),
  shellEmailNewWork: draft("जब नया काम मेरा इंतज़ार करे"),
  shellEmailDigest: draft("हर सुबह, क्या मेरा इंतज़ार कर रहा है और किसमें देर हुई"),
  shellEmailAddress: draft("इस ईमेल पर"),
  shellEmailSave: draft("सहेजें"),
  shellEmailSaved: draft("सहेज लिया।"),
  shellEmailNeedsAddress: draft("जिस ईमेल पर भेजना है वह दें, फिर सहेजें।"),
  shellEmailCodeSent: draft(
    (email) =>
      `हमने ${email} पर एक कोड ईमेल किया है। उसे यहाँ डालें: जब तक आप नहीं डालते, आपके काम के बारे में वहाँ कुछ नहीं जाता।`,
  ),
  shellEmailCode: draft("ईमेल में आया कोड"),
  shellEmailConfirm: draft("पुष्टि करें"),
  shellEmailConfirmed: draft("पुष्टि हो गई: ईमेल इसी पते पर जाएँगे।"),
  shellEmailWrongCode: draft(
    "वह कोड नहीं चला। उसे जाँचें: उसकी अवधि खत्म होने या बहुत बार आज़माए जाने पर, नए कोड के लिए फिर से सहेजें।",
  ),
  shellEmailSaveForCode: draft("इस पते के लिए कोई कोड इंतज़ार में नहीं है: कोड पाने के लिए सहेजें।"),
  shellEmailCodeWaiting: draft(
    (email: string) =>
      `सहेज लिया। ${email} पर पहले भेजा गया कोड डालें: जब तक वह चलता है, फिर से सहेजने पर नया कोड नहीं जाता।`,
  ),
  shellEmailCodeNotSent: draft("सहेज लिया, पर इस बार कोई कोड ईमेल नहीं हुआ। कोड पाने के लिए बाद में फिर से सहेजें।"),
  shellEmailCodeHeld: draft(
    "सहेज लिया। LAMP एक घंटे में जितने कोड भेजता है, उतने आपको मिल चुके हैं: एक घंटे बाद फिर से सहेजें, तब कोड आएगा।",
  ),
  shellNothingNew: draft("कुछ नया नहीं।"),
  shellNotificationsCount: draft((count) => `सूचनाएँ: ${count} नई`),
  shellNewWork: draft("आपके लिए नया"),
  shellLateWork: draft("देर हो गई"),
  shellNewMessages: draft("संदेश"),
  shellAccount: draft("खाता"),
  shellOtherLanguage: draft("View in English"),
  shellPreferences: draft("आपकी पसंद"),
  shellSignOut: draft("साइन आउट"),
  shellMenu: draft("मेनू"),
  shellClose: draft("बंद करें"),
  shellTabs: draft("टैब"),
  shellSoon: draft("यह पेज अभी बना नहीं है। तब तक चैट में LAMP से पूछें।"),
  shellOpenChat: draft("LAMP से बात करें"),
  shellSettingsTabs: draft("सेटिंग्स"),
  shellBusinessSettings: draft("बिज़नेस सेटिंग्स"),
  shellPrivacy: draft("गोपनीयता"),

  // Signing in.
  signInTitle: draft("LAMP में साइन इन करें"),
  signInEmail: draft("ईमेल"),
  signInEmailMe: draft("मुझे कोड ईमेल करें"),
  signInSent: draft((email) => `हमने ${email} पर कोड भेजा है। यह 10 मिनट तक चलेगा।`),
  signInCode: draft("कोड"),
  signInTrust: draft("इस डिवाइस पर मुझे साइन इन रखें"),
  signInSubmit: draft("साइन इन"),
  signInOtherEmail: draft("दूसरा ईमेल इस्तेमाल करें"),
  signInTooMany: draft("बहुत ज़्यादा कोड माँगे गए। कुछ मिनट रुककर फिर कोशिश करें।"),
  signInCheck: draft("ईमेल जाँचें, फिर कोशिश करें।"),
  signInChoose: draft("कौन सा कारोबार?"),
  signInChooseIntro: draft("आपका ईमेल LAMP पर एक से ज़्यादा कारोबारों में है। जिसमें साइन इन करना है, उसे चुनें।"),
  invoiceOpening: draft("इनवॉइस खुल रहा है…"),
  invoiceGone: draft(
    "यह लिंक समाप्त हो गया है या हमारा भेजा हुआ नहीं है। जिस व्यवसाय ने इनवॉइस भेजा था, उससे फिर माँगें।",
  ),
  invoiceTitle: draft((number: string, business: string) => `${business} का इनवॉइस ${number}`),
  invoiceFor: draft((customer: string, date: string) => `${customer} के लिए, दिनांक ${date}।`),
  invoicePrinting: draft("इसका PDF छप रहा है। कुछ मिनट बाद यह लिंक फिर खोलें।"),
  quotationOpening: draft("कोटेशन खुल रहा है…"),
  quotationGone: draft(
    "यह लिंक समाप्त हो गया है या हमारा भेजा हुआ नहीं है। जिस व्यवसाय ने कोटेशन भेजा था, उससे फिर माँगें।",
  ),
  quotationUnreachable: draft("अभी व्यवसाय तक नहीं पहुँच पाए। थोड़ी देर में फिर कोशिश करें।"),
  quotationTitle: draft((number: string, business: string) => `${business} का कोटेशन ${number}`),
  quotationFor: draft(
    (customer: string, date: string, validUntil: string) =>
      `${customer} के लिए, दिनांक ${date}, ${validUntil} तक मान्य।`,
  ),
  quotationItem: draft("आइटम"),
  quotationQuantity: draft("मात्रा"),
  quotationRate: draft("दर"),
  quotationAmount: draft("राशि"),
  quotationTotal: draft((total: string) => `कुल ${total}, GST इनवॉइस की तारीख़ पर लागू दर से अलग।`),
  quotationPdf: draft("PDF डाउनलोड करें"),
  quotationAccept: draft("स्वीकार करें"),
  quotationAsk: draft("सवाल पूछें"),
  quotationDecline: draft("मना करें"),
  quotationQuestion: draft("आपका सवाल"),
  quotationSend: draft("भेजें"),
  quotationBack: draft("वापस"),
  quotationAnswered: draft((answer: "accept" | "question" | "decline", business: string) =>
    answer === "accept"
      ? `धन्यवाद। ${business} को आपकी स्वीकृति मिल गई है और वे आपके साथ ऑर्डर की पुष्टि करेंगे।`
      : answer === "decline"
        ? `${business} को बताने के लिए धन्यवाद।`
        : `धन्यवाद। ${business} को आपका सवाल मिल गया है और वे आपको जवाब देंगे।`,
  ),
  invitationTitle: draft("LAMP पर आपका न्योता"),
  invitationOpening: draft("आपका न्योता खुल रहा है…"),
  invitationFor: draft(
    (business: string | null, roles: readonly string[]) =>
      `${business ?? "आपके कारोबार"} ने आपको LAMP पर${roles.length > 0 ? ` ${roles.join(", ")} के रूप में` : ""} बुलाया है।`,
  ),
  invitationAccept: draft("स्वीकार करें और साइन इन करें"),
  invitationGone: draft(
    "यह न्योता लिंक पहले ही इस्तेमाल हो चुका है, इसकी समय-सीमा खत्म हो गई है, या इसकी जगह नया लिंक भेजा गया है। जिसने बुलाया, उनसे इसे फिर भेजने को कहें, या अपने ईमेल से साइन इन करें।",
  ),
  invitationTelegram: draft("यह न्योता Telegram के लिए है। इसका लिंक उस फ़ोन पर खोलें जिसका नंबर बुलाया गया है।"),
  invitationSignIn: draft("अपने ईमेल से साइन इन करें"),
  invitationPin: draft("एक PIN, जब कोड न आए (ज़रूरी नहीं)"),
  invitationWeakPin: draft(
    "यह PIN आसानी से अंदाज़ा लगाया जा सकता है। छह ऐसे अंक चुनें जो कोई पैटर्न न हों, और फिर कोशिश करें।",
  ),
  invitationPinSixDigits: draft("PIN छह अंकों का होता है। छह अंक लिखें, या इसे खाली छोड़ दें।"),
  invitationTooMany: draft("बहुत ज़्यादा कोशिशें हुईं। कुछ मिनट रुककर फिर से स्वीकार करें।"),
  invitationNoPin: draft("PIN के बिना स्वीकार करें"),
  pinConsentHeading: draft("क्या LAMP आपका PIN रख सकता है?"),
  pinConsentMessage: draft(
    "आपका PIN संवेदनशील डेटा है। LAMP इसे केवल आपकी सहमति से रखता है, एकतरफ़ा हैश के रूप में जिसे वह वापस नहीं पढ़ सकता, ताकि कोड न आने पर पुष्टि हो सके कि आप ही हैं। आप सेटिंग्स → गोपनीयता में यह सहमति वापस ले सकते हैं, जिससे PIN मिट जाता है।",
  ),
  pinConsentNotice: draft("गोपनीयता सूचना पढ़ें"),
  pinConsentConfirm: draft("मैं सहमत हूँ, मेरा PIN रखें"),
  signInNotKept: draft("साइन इन हो गया, पर सेशन नहीं रुका। देखें कि कुकीज़ चालू हैं।"),
  signInBadCode: draft("यह कोड नहीं चला। इसे जाँचें, या नया कोड माँगें।"),

  // Today.
  todayLoading: draft("आज का काम लोड हो रहा है…"),
  todayFailed: draft("आज का काम लोड नहीं हो सका। थोड़ी देर में फिर कोशिश करें।"),
  todayRefused: draft("LAMP ने इसे मना कर दिया।"),
  todayNothing: draft("अभी आपके लिए कुछ नहीं है।"),
  todayCount: draft((count) => (count === 1 ? "आपके लिए 1 फ़ैसला।" : `आपके लिए ${count} फ़ैसले।`)),
  todayBiggest: draft((amount, label, who) => {
    const kind = label === "overdue" ? "बकाया" : label === "to bill" ? "बिल करना है" : "ऑर्डर मूल्य";
    return `सबसे बड़ा ${amount} ${kind}${who === undefined ? "" : ` (${who})`}।`;
  }),
  todayQueue: draft("आपके फ़ैसले"),
  todayMarkDone: draft("हो गया"),
  todayReview: draft("देखें और पक्का करें"),
  todaySeeWrong: draft("क्या गड़बड़ है, देखें"),
  todayMore: draft("और जानकारी"),
  todayCodes: draft("संदर्भ कोड"),
  todayOpenRun: draft("रन खोलें"),
  todayOpenBreakdown: draft("लागत का ब्यौरा खोलें"),
  todayNoteMargin: draft("नोट करें और काम जारी रखें"),
  todayMarginNote: draft("आपका नोट (वैकल्पिक)"),
  todayStartRateRevision: draft("ग्राहक के साथ रेट संशोधन शुरू करें"),
  todayCall: draft((name) => `${name} को कॉल करें`),
  todayNoticed: draft("LAMP ने देखा"),
  todayReminders: draft("रिमाइंडर"),
  todayReminderFrom: draft((text: string, from: string) => `${text} (${from} की ओर से)`),
  todayIssued: draft("हाल में जारी"),
  todayNoIssued: draft("अभी कोई दस्तावेज़ जारी नहीं हुआ।"),
  todayIssuedTag: draft("जारी"),
  todayInvoice: draft("इनवॉइस"),
  todayPdf: draft("(PDF)"),
  todayAmountOverdue: draft("बकाया"),
  todayAmountToBill: draft("बिल करना है"),
  todayAmountOrderValue: draft("ऑर्डर मूल्य"),

  // Chat with LAMP.
  chatThread: draft("LAMP से बातचीत"),
  chatLoading: draft("बातचीत लोड हो रही है…"),
  chatEmpty: draft(
    "नीचे ऑर्डर लिखें, जैसे “श्री मोटर्स के लिए 10 हेक्स बोल्ट ₹12.50 पर”। LAMP जाँच के लिए ड्राफ़्ट बनाएगा।",
  ),
  chatYou: draft("आप"),
  chatLamp: draft("LAMP"),
  chatMessage: draft("LAMP को संदेश"),
  chatPlaceholder: draft("ऑर्डर या सवाल लिखें"),
  chatSend: draft("भेजें"),
  chatSending: draft("भेज रहे हैं…"),
  chatOwnerOnly: draft("सिर्फ़ Owner ही LAMP के एजेंट रोक या फिर से चला सकता है।"),
  chatAskOwner: draft("बिज़नेस के किसी Owner से कहें।"),
  chatWorking: draft(
    (held) => `LAMP के एजेंट फिर से काम कर रहे हैं।${held > 0 ? ` रुके हुए ${held} संदेश भेजे जा रहे हैं।` : ""}`,
  ),
  chatChips: draft("झटपट जवाब"),
  chatOpenPage: draft((page: string) => `${page} खोलें`),
  chatAddThem: draft("जोड़ें और ऑर्डर का ड्राफ़्ट बनाएँ"),
  chatAddThemText: draft("जोड़ दो"),
  chatChipOrder: draft("नया ऑर्डर"),
  chatChipOrderText: draft("श्री मोटर्स के लिए 10 हेक्स बोल्ट ₹12.50 पर"),
  chatChipPrivacy: draft("गोपनीयता"),

  chatAttach: draft("फ़ाइल जोड़ें"),
  chatCamera: draft("फ़ोटो लें"),
  chatAttached: draft("भेजने वाली फ़ाइलें"),
  chatRemoveFile: draft((name) => `${name} हटाएँ`),
  chatDropHint: draft("फ़ाइलें LAMP को भेजने के लिए यहाँ छोड़ें"),
  chatFileType: draft(
    (name) => `${name} नहीं भेजी जा सकती: LAMP PDF, JPEG, PNG, XLSX और CSV फ़ाइलें, और Tally एक्सपोर्ट की XML लेता है।`,
  ),
  chatFileSize: draft((name, megabytes) => `${name} ${megabytes} MB से बड़ी है, इसलिए भेजी नहीं जा सकती।`),
  chatFileEmpty: draft((name) => `${name} खाली है।`),
  chatFileRecovery: draft("कोई दूसरी फ़ाइल चुनें, या उसकी फ़ोटो भेजें।"),
  chatOpenFile: draft((name) => `${name} खोलें`),
  fileKind: draft(
    (kind) =>
      ({
        customer_po: "ग्राहक का परचेज़ ऑर्डर",
        supplier_bill: "सप्लायर का बिल",
        sales_invoice: "हमारा पुराना बिक्री इनवॉइस",
        stock_register: "स्टॉक शीट",
        drawing: "ड्रॉइंग",
        joining_form: "ज्वाइनिंग फ़ॉर्म",
        attendance_register: "हाजिरी रजिस्टर",
        gate_pass: "गेट एंट्री",
        delivery_challan: "सप्लायर का डिलीवरी चालान",
        job_work_return: "जॉब वर्कर का वापसी चालान",
        stock_count: "स्टॉक गिनती शीट",
        bank_statement: "बैंक स्टेटमेंट",
        complaint_note: "ग्राहक की शिकायत",
      })[kind] ?? kind,
  ),
  fileCard: draft((name) => `फ़ाइल ${name}`),
  fileFrom: draft((party) => `किससे या किसके लिए: ${party}`),
  fileYes: draft("हाँ"),
  fileSomethingElse: draft("यह कुछ और है"),
  fileWhatIsIt: draft("यह क्या है?"),
  fileNone: draft("इनमें से कोई नहीं"),
  fileRetry: draft("फिर से कोशिश करें"),
  fileRecord: draft("दर्ज करें"),
  fileRead: draft("LAMP ने क्या पढ़ा"),
  fileField: draft(
    (key) =>
      ({
        supplierName: "सप्लायर",
        supplierGstin: "सप्लायर का GSTIN",
        billNumber: "बिल नंबर",
        billDate: "बिल की तारीख़",
        buyerName: "ग्राहक",
        buyerGstin: "ग्राहक का GSTIN",
        invoiceNumber: "इनवॉइस नंबर",
        invoiceDate: "इनवॉइस की तारीख़",
        taxableValuePaise: "कर योग्य मूल्य",
        gstPaise: "GST",
        totalPaise: "कुल",
        drawingNumber: "ड्रॉइंग नंबर",
        title: "शीर्षक",
        revision: "रिविज़न",
        material: "सामग्री",
        itemName: "आइटम",
        quantity: "मात्रा",
        unit: "इकाई",
        unitPricePaise: "दर",
        date: "तारीख़",
        quantityIn: "आया",
        quantityOut: "गया",
        learningNumber: "डॉक्यूमेंट लर्निंग",
        name: "नाम",
        kind: "ग्राहक या सप्लायर",
        gstin: "GSTIN",
        salePricePaise: "दर",
        customer: "ग्राहक",
        gateEntryNumber: "गेट एंट्री नंबर",
        partyName: "किससे",
        vehicleNumber: "गाड़ी",
        documentNumber: "साथ आया काग़ज़",
        orderReference: "उस पर लिखा ऑर्डर",
        challanNumber: "चालान नंबर",
        challanDate: "चालान की तारीख़",
        poNumber: "हमारा PO नंबर",
        valuePaise: "मूल्य",
        jobWorkerName: "जॉब वर्कर",
        ourChallanNumber: "हमारा चालान नंबर",
        against: "किसके सामने",
        check: "जाँचें",
        open: "बाक़ी",
        sent: "भेजा",
        withJobWorker: "जॉब वर्कर के पास",
        returned: "लौटा",
        rejected: "रिजेक्ट",
        scrap: "स्क्रैप",
        withJobWorkerAfter: "बाद में जॉब वर्कर के पास",
        location: "जगह",
        counted: "गिना",
        book: "बही स्टॉक",
        difference: "अंतर",
        account: "खाता",
        periodFrom: "से",
        periodTo: "तक",
        openingBalancePaise: "शुरुआती बैलेंस",
        closingBalancePaise: "अंतिम बैलेंस",
        matched: "मिलान हुई पंक्तियाँ",
        unmatched: "बिना मिलान की पंक्तियाँ",
        line: "पंक्ति",
        description: "विवरण",
        reference: "रेफ़रेंस",
        creditPaise: "आया पैसा",
        debitPaise: "गया पैसा",
        match: "किससे मिला",
        party: "ग्राहक या सप्लायर",
        settles: "किसका भुगतान",
        reason: "क्यों नहीं",
      })[key] ?? key,
  ),
  fileCheckDrawing: draft(
    "LAMP ने जो नंबर, रिविज़न और ग्राहक पढ़ा है उसे जाँचें; ज़रूरत हो तो सुधारें, फिर दर्ज करें।",
  ),
  fileDevelop: draft((drawing, customer) => `${customer} के लिए पार्ट ${drawing} डेवलप करें`),
  fileMatch: draft(
    (match) =>
      ({
        receipt: "पहले से दर्ज रसीद",
        invoices: "खुले इनवॉइस",
        payment: "पहले से दर्ज भुगतान",
        bill: "खुला बिल",
      })[match] ?? match,
  ),
  fileKeep: draft("इसे किसके साथ रखें…"),
  fileKeepAgainst: draft("इसके साथ रखें"),
  fileKeepObject: draft(
    (object) =>
      ({
        customer: "ग्राहक",
        supplier: "सप्लायर",
        item: "आइटम",
        sales_order: "सेल्स ऑर्डर",
        purchase_order: "परचेज़ ऑर्डर",
        run: "रन",
      })[object] ?? object,
  ),
  fileKeepName: draft("नाम या नंबर"),
  fileKeepWords: draft("इसके बारे में एक पंक्ति"),
  fileKeepSubmit: draft("रख दें"),
  fileKept: draft((what, label, words) => `${what} ${label} के साथ रखा: “${words}”`),
  fileReason: draft("क्यों"),
  fileRecordAnyway: draft("फिर भी दर्ज करें"),
  fileSendBack: draft("वापस भेजें"),
  fileSentBack: draft((reason) => `वापस भेजा: ${reason}`),

  fileDataImport: draft("लाने के लिए आपका डेटा"),
  fileImportConfirm: draft("पक्का करें और ले आएँ"),
  fileImportPreview: draft((number, asOf) => `डेटा इम्पोर्ट ${number}, ${asOf} को`),
  fileImportCustomers: draft("ग्राहक"),
  fileImportSuppliers: draft("सप्लायर"),
  fileImportJobWorkers: draft("जॉब वर्कर"),
  fileImportItems: draft("आइटम"),
  fileImportNew: draft((n) => `${n} नए`),
  fileImportBalances: draft("ओपनिंग बैलेंस"),
  fileImportOwedToYou: draft("आपको मिलना है"),
  fileImportYouOwe: draft("आपको देना है"),
  fileImportStock: draft("ओपनिंग स्टॉक"),
  fileImportPans: draft("PAN, पक्का करने पर सुरक्षित रखे जाएँगे"),
  fileImportLinked: draft("पहले से LAMP में, जोड़े गए"),
  fileImportItem: draft("आइटम"),
  fileImportQuantity: draft("मात्रा"),
  fileImportUnit: draft("इकाई"),
  fileImportAlready: draft((n) => `${n} पहले से LAMP में हैं, छोड़ दिए गए ताकि कुछ दो बार न जुड़े`),
  fileImportNotBrought: draft("नहीं लाए गए"),
  fileImportWhat: draft("कहाँ"),
  fileImportName: draft("नाम"),
  fileImportWhy: draft("क्यों"),
  fileImportDone: draft("ले आए"),

  setUpFrom: draft((drawing, revision) => `ड्रॉइंग ${drawing} रिविज़न ${revision} से बना ड्राफ़्ट`),
  setUpUnmatched: draft("LAMP इनका मिलान नहीं कर सका"),
  setUpUnmatchedHelp: draft("रिकॉर्ड में इन्हें करने वाला कोई नहीं है। इन्हें जोड़ें, या LAMP के बाहर इंतज़ाम करें।"),
  setUpPart: draft("पार्ट"),
  setUpNewPart: draft("नए पार्ट का नाम"),
  setUpPartUnit: draft("गिनती की इकाई"),
  setUpMaterials: draft("एक पीस के लिए सामग्री"),
  setUpLine: draft((n) => `लाइन ${n}`),
  setUpItem: draft("आइटम"),
  setUpQuantity: draft("मात्रा"),
  setUpUnit: draft("इकाई"),
  setUpScrap: draft("स्क्रैप %"),
  setUpAddLine: draft("लाइन जोड़ें"),
  setUpRemoveLine: draft((n) => `लाइन ${n} हटाएँ`),
  setUpOperations: draft("ऑपरेशन"),
  setUpOperation: draft((n) => `ऑपरेशन ${n}`),
  setUpSequence: draft("क्रम"),
  setUpName: draft("ऑपरेशन"),
  setUpWorkCentre: draft("वर्क सेंटर"),
  setUpSetupMinutes: draft("सेटअप मिनट"),
  setUpCycleMinutes: draft("प्रति पीस मिनट"),
  setUpAddOperation: draft("ऑपरेशन जोड़ें"),
  setUpRemoveOperation: draft((n) => `ऑपरेशन ${n} हटाएँ`),
  setUpChoose: draft("चुनें…"),
  setUpJobWork: draft("जॉब वर्कर को भेजा गया"),

  // A card in the thread.
  cardNeedsYou: draft("आपकी ज़रूरत"),
  cardYourTask: draft("आपका काम"),
  cardBlocked: draft("रुका हुआ"),
  cardWorking: draft("LAMP कर रहा है"),
  cardDone: draft("हो गया"),
  fileNotDone: draft("नहीं हुआ"),
  cardPicked: draft("स्टॉक से निकालकर दे दिया"),
  cardHandedOver: draft("माल के साथ सौंप दिया"),
  cardDoneIt: draft("कर दिया"),
  cardReceivedBy: draft("माल और दस्तावेज़ किसने लिए"),
  cardHandedOverOn: draft("सौंपने की तारीख"),
  cardTransportDocument: draft("ट्रांसपोर्ट दस्तावेज़ (LR या डॉकेट नंबर), अगर हो"),
  cardEwayBillNumber: draft("पोर्टल से मिला ई-वे बिल नंबर (12 अंक)"),
  cardEwayBillValidUntil: draft("कब तक मान्य"),
  cardCommittedDate: draft("ग्राहक को वादा की गई डिलीवरी की तारीख"),
  cardGstRate: draft("GST दर (%), जैसे 18"),
  cardSendBack: draft("वापस भेजें"),
  cardSeePrint: draft("छपने पर कैसा दिखेगा"),
  cardOpenPdf: draft((number) => `${number} का PDF खोलें`),
  cardConfirm: draft("पक्का करें"),
  cardWhatChange: draft("क्या बदलना है? (ज़रूरी नहीं)"),
  cardReleaseWhy: draft("इस ऑर्डर पर रोक क्यों हटा रहे हैं?"),
  cardOverdue: draft("बकाया बिल"),
  cardFiles: draft("काग़ज़"),
  cardOpenFile: draft((name: string) => `${name} खोलें`),
  cardEmailTo: draft((name: string) => `${name} को ईमेल, आपकी पुष्टि के बाद:`),
  cardEmailNoAddress: draft((name: string) => `${name} का कोई ईमेल दर्ज नहीं है: पुष्टि से पहले एक जोड़ें।`),
  cardRelease: draft("इस ऑर्डर पर रोक हटाएं"),
  cardCancel: draft("रद्द करें"),
  cardAddRow: draft("एक लाइन जोड़ें"),
  cardRemoveRow: draft((n: number) => `लाइन ${String(n)} हटाएं`),
  cardChoose: draft("चुनें"),
  cardFindOption: draft((label: string) => `${label} खोजने के लिए लिखें`),
  cardLeftToAllocate: draft((amount: string) => `${amount} बाँटना बाकी है`),
  cardNotAnAmount: draft((line: string) => `${line} रकम नहीं है: 30,000 या 4999.50 की तरह लिखें`),
  cardOverAllocated: draft((amount: string) => `कुल से ${amount} ज़्यादा: किसी लाइन से घटाएँ`),
  cardFileKind: draft("सिर्फ़ PDF, JPEG या PNG"),
  cardFileEmpty: draft("यह फ़ाइल खाली है"),
  cardFileTooLarge: draft("यह फ़ाइल 5 MB से बड़ी है"),
  cardPrintTitle: draft((number) => `इनवॉइस ${number} छपने पर`),
  cardInvoice: draft((number) => `इनवॉइस ${number}`),

  // lists/List.tsx
  listSearch: draft("खोजें"),
  listLoading: draft((what: string) => `${what} लोड हो रहे हैं…`),
  listForbidden: draft((what: string) => `आपके रोल में ${what} देखने की अनुमति नहीं है।`),
  listFailed: draft((what: string) => `${what} लोड नहीं हो सके। थोड़ी देर में फिर कोशिश करें।`),
  listPages: draft((what: string) => `${what} के पेज`),
  listPrevious: draft("पिछला"),
  listNext: draft("अगला"),
  listPageOf: draft(
    (page: number, pages: number, total: number, what: string) => `पेज ${page} / ${pages} · ${total} ${what}`,
  ),

  // documents/Documents.tsx
  documentsTitle: draft("दस्तावेज़"),
  documentsIntro: draft("LAMP के जारी किए सभी इनवॉइस। जारी दस्तावेज़ कभी नहीं बदलते।"),
  documentsFrom: draft("से"),
  documentsTo: draft("तक"),
  documentsSearch: draft("दस्तावेज़ खोजें"),
  documentsWhat: draft("दस्तावेज़"),
  documentsNoMatch: draft("कोई दस्तावेज़ नहीं मिला।"),
  documentsNone: draft("LAMP ने अभी तक कोई दस्तावेज़ जारी नहीं किया।"),
  documentsDocument: draft("दस्तावेज़"),
  documentsCustomer: draft("ग्राहक"),
  documentsDate: draft("तारीख"),
  documentsAmount: draft("रकम"),
  documentsTaxInvoice: draft("टैक्स इनवॉइस"),
  documentsTallyTitle: draft("Tally एक्सपोर्ट"),
  documentsTallyIntro: draft(
    "एक महीने की अकाउंटिंग एंट्री Tally वाउचर के रूप में, आपके CA के लिए, Tally में Import > Vouchers से लाने के लिए। मालिक और अकाउंटेंट के लिए।",
  ),
  documentsTallyMonth: draft("महीना"),
  documentsTallyDownload: draft((month) => `${month} का Tally एक्सपोर्ट डाउनलोड करें`),
  booksLinkTitle: draft("Tally वाउचर"),
  booksLinkInLamp: draft("आपकी वैधानिक बहियाँ LAMP में रखी जाती हैं।"),
  booksLinkKeepInTally: draft("बहियाँ Tally में रखें"),
  booksLinkKeepInLamp: draft("बहियाँ LAMP में रखें"),
  booksLinkIntro: draft(
    (since) =>
      `आपकी वैधानिक बहियाँ Tally में रखी जाती हैं। ${since} से हर पोस्टिंग एक वाउचर के रूप में वहाँ जाती है, एक फ़ाइल में जिसे आप Tally में Import > Vouchers से लाते हैं।`,
  ),
  booksLinkUnsent: draft((count) =>
    count === 0 ? "हर वाउचर भेजा जा चुका है।" : `${count} वाउचर अभी नहीं भेजे गए हैं।`,
  ),
  booksLinkMake: draft("न भेजे गए वाउचरों की फ़ाइल बनाएँ"),
  booksLinkNothing: draft("भेजने के लिए कोई वाउचर नहीं है।"),
  booksLinkMade: draft((name, count) => `${name}: ${count} वाउचर।`),
  booksLinkHeld: draft("फ़ाइल में नहीं, जब तक जो छूटा है वह Settings → Ledger Mapping में मैप न हो:"),
  booksLinkFile: draft("फ़ाइल"),
  booksLinkVouchers: draft("वाउचर"),
  booksLinkMadeAt: draft("बनी"),
  booksLinkAgain: draft("फिर से बनाएँ"),
  booksLinkAgainOf: draft((number) => `फ़ाइल ${number} फिर से`),
  booksReconciliationTitle: draft("बहियों का मिलान"),
  booksReconciliationIntro: draft(
    "LAMP और Tally का वाउचर-दर-वाउचर मिलान, उस डे बुक से जो आप Tally में (Display > Day Book, XML में एक्सपोर्ट) निकालकर चैट में भेजते हैं। इससे कुछ भी बुक या बदला नहीं जाता।",
  ),
  booksReconciliationPeriod: draft((from, to) => `${from} से ${to}`),
  booksReconciliationVersion: draft((version, at, dayBook) => `संस्करण ${version}, ${at}, ${dayBook} से`),
  booksReconciliationStatus: draft({
    matched: "मिलान हुआ",
    differs: "अंतर है",
    only_in_lamp: "LAMP में है, Tally में नहीं",
    only_in_tally: "Tally में है, LAMP में नहीं",
    twice: "Tally में दो बार",
  }),
  booksReconciliationDifference: draft({ amount: "राशि", ledger: "लेजर", cost_centre: "कॉस्ट सेंटर" }),
  booksReconciliationAllMatched: draft((count) =>
    count === 1
      ? "एक वाउचर LAMP और Tally दोनों में है, और मेल खाता है।"
      : `सभी ${count} वाउचर LAMP और Tally दोनों में हैं, और मेल खाते हैं।`,
  ),
  booksReconciliationSheet: draft("शीट डाउनलोड करें"),
  booksReconciliationShow: draft("पंक्तियाँ दिखाएँ"),
  booksReconciliationAgain: draft("फिर से मिलान करें"),
  booksReconciliationBill: draft("बिल या वाउचर"),
  booksReconciliationLamp: draft("LAMP"),
  booksReconciliationTally: draft("टैली (Tally)"),
  booksReconciliationLookAt: draft("अकाउंट्स Role के देखने के लिए: यह बिल नहीं है।"),

  // parties/Parties.tsx
  partiesTitle: draft("ग्राहक और सप्लायर"),
  partiesIntro: draft("जिनसे आप खरीदते हैं, जिन्हें बेचते हैं या जॉब वर्क भेजते हैं।"),
  partiesShow: draft("दिखाएँ"),
  partiesAll: draft("सभी"),
  partiesCustomers: draft("ग्राहक"),
  partiesSuppliers: draft("सप्लायर"),
  partiesJobWorkers: draft("जॉब वर्कर"),
  partiesCustomer: draft("ग्राहक"),
  partiesSupplier: draft("सप्लायर"),
  partiesJobWorker: draft("जॉब वर्कर"),
  partiesSearch: draft("ग्राहक और सप्लायर खोजें"),
  partiesWhat: draft("ग्राहक और सप्लायर"),
  partiesNoMatch: draft((q: string) => `“${q}” से कोई नहीं मिला।`),
  partiesNone: draft("अभी यहाँ कोई नहीं है।"),
  partiesName: draft("नाम"),
  partiesKind: draft("प्रकार"),
  partiesGstin: draft("GSTIN"),
  partiesPhone: draft("फ़ोन"),
  partiesPaysIn: draft("भुगतान"),
  partiesDays: draft((days: number) => `${days} दिन`),

  // items/Items.tsx
  itemsTitle: draft("आइटम और स्टॉक"),
  itemsIntro: draft("तैयार पार्ट, कच्चा माल और कंज़्यूमेबल, स्टोर में कितना है उसके साथ।"),
  itemsShow: draft("दिखाएँ"),
  itemsAll: draft("सभी"),
  itemsInStock: draft("स्टॉक में"),
  itemsOutOfStock: draft("स्टॉक ख़त्म"),
  itemsSearch: draft("आइटम खोजें"),
  itemsWhat: draft("आइटम"),
  itemsNoMatch: draft((q: string) => `“${q}” से कोई आइटम नहीं मिला।`),
  itemsNone: draft("यहाँ कोई आइटम नहीं है।"),
  itemsItem: draft("आइटम"),
  itemsHsn: draft("HSN"),
  itemsGstRate: draft("GST दर"),
  itemsStock: draft("स्टॉक में"),
  itemsRates: draft("खरीद / बिक्री रेट"),
  itemsRatesOf: draft((cost: string, price: string, unit: string) => `${cost} / ${price} प्रति ${unit}`),
  itemsCost: draft("लागत"),
  itemsSalePrice: draft("बिक्री रेट"),
  itemsRateOf: draft((rate: string, unit: string) => `${rate} प्रति ${unit}`),
  itemsHeld: draft("ऑर्डरों के लिए रखा"),
  itemsReorder: draft("रीऑर्डर लेवल"),
  itemsReorderOf: draft(
    (level: string, order: string | undefined, unit: string) =>
      `${level} ${unit}${order === undefined ? "" : `, ऑर्डर ${order} ${unit}`}`,
  ),

  // parties/MasterDataForm.tsx: adding and correcting customers, suppliers, job workers and Items
  masterAdd: draft("जोड़ें"),
  masterEdit: draft("बदलें"),
  masterActions: draft("कार्रवाई"),
  masterEditOf: draft((name: string) => `${name} बदलें`),
  masterSave: draft("सहेजें"),
  masterAddParty: draft("ग्राहक, सप्लायर या जॉब वर्कर जोड़ें"),
  masterAddItem: draft("आइटम जोड़ें"),
  masterName: draft("नाम"),
  masterKind: draft("प्रकार"),
  masterGstin: draft("GSTIN"),
  masterAddress: draft("पता"),
  masterState: draft("राज्य"),
  masterPhone: draft("फ़ोन"),
  masterEmail: draft("ईमेल"),
  masterTerms: draft("भुगतान अवधि, दिनों में"),
  masterItemKind: draft("प्रकार"),
  masterItemKindNone: draft("नहीं बताया"),
  masterFinishedPart: draft("तैयार पार्ट"),
  masterRawMaterial: draft("कच्चा माल"),
  masterConsumable: draft("उपभोग्य सामग्री"),
  masterUnit: draft("यूनिट, जैसे kg या piece"),
  masterHsn: draft("HSN"),
  masterGstRate: draft("GST दर (%)"),
  masterGstRateHelp: draft(
    "जहाँ LAMP के पास HSN की दर नहीं है, वहाँ यही लगती है; जहाँ LAMP के पास है, वहाँ LAMP की दर लगती है।",
  ),
  masterSupplier: draft("किससे खरीदते हैं"),
  masterOpening: draft("ओपनिंग स्टॉक"),
  masterOpeningHelp: draft("स्टॉक लेजर में एक ही बार दर्ज होता है।"),
  masterOpeningUnit: draft("किस यूनिट में गिना, अगर आइटम की यूनिट नहीं"),
  masterFailed: draft("अभी सहेज नहीं पाए। फिर से कोशिश करें।"),
  masterConfirm: draft("पक्का करें"),
  masterCorrect: draft("सुधारें"),
  masterRecorded: draft("दर्ज हो गया"),

  taxTitle: draft("टैक्स विवरण"),
  taxPan: draft("PAN"),
  taxPanNone: draft("दर्ज नहीं"),
  taxPanMasked: draft("छिपाकर दिखाया गया: आपकी भूमिकाओं के पास पहचान नंबर देखने का अधिकार नहीं है।"),
  taxPanNew: draft("PAN दर्ज करें"),
  taxPanHelp: draft("यह वही PAN होना चाहिए जो GSTIN में है। दर्ज PAN रखने के लिए खाली छोड़ें।"),
  taxPanClear: draft("PAN हटाएँ"),
  taxNature: draft("भुगतान की सामान्य प्रकृति"),
  taxNatureNone: draft("नहीं बताया"),
  taxNatureWords: draft((id: string, words: string) => NATURE_HI[id] ?? words),
  taxCertificate: draft("कम कटौती प्रमाणपत्र"),
  taxCertificateHelp: draft("अगर सप्लायर के पास कोई नहीं है तो सब खाली छोड़ें।"),
  taxCertificateNumber: draft("प्रमाणपत्र नंबर"),
  taxCertificateRate: draft("इसमें मान्य TDS दर (%)"),
  taxCertificateLimit: draft("अधिकतम (₹)"),
  taxCertificateFrom: draft("कब से मान्य"),
  taxCertificateTo: draft("कब तक मान्य"),
  taxSave: draft("टैक्स विवरण सहेजें"),
  taxBankDetails: draft("बैंक विवरण केवल मालिक ही, खुद, नए कोड के साथ बदलते हैं।"),
  taxFailed: draft("अभी टैक्स विवरण पढ़ नहीं पाए।"),

  bomGive: draft("BOM दें"),
  bomButton: draft("BOM"),
  bomOf: draft((name) => `${name} का बिल ऑफ़ मटीरियल्स और रूटिंग`),
  bomForOne: draft((part, unit) => `${part} की एक ${unit} किससे बनती है`),
  bomRouting: draft("रूटिंग, क्रम से"),
  bomOutsourced: draft("जॉब वर्कर के यहाँ"),
  bomJobWorker: draft("जॉब वर्कर"),
  bomNone: draft("अभी कोई बिल ऑफ़ मटीरियल्स नहीं।"),
  bomSave: draft("BOM सहेजें"),
  bomReadOnly: draft("आपके रोल इसे पढ़ने देते हैं; इसे फ़्लोर सुपरवाइज़र, ऑफ़िस मैनेजर या मालिक बदलते हैं।"),
  bomFailed: draft("अभी बिल ऑफ़ मटीरियल्स पढ़ा नहीं जा सका। फिर कोशिश करें।"),
  bomNotYours: draft("इस पार्ट का बिल ऑफ़ मटीरियल्स और रूटिंग पढ़ना आपके लिए नहीं है: आपके रोल इसकी अनुमति नहीं देते।"),

  keptFilesTitle: draft("फ़ाइलें"),
  keptFilesOfOrder: draft("ऑर्डर की फ़ाइलें"),
  keptFilesButton: draft("फ़ाइलें"),
  keptFilesOf: draft((name) => `${name} के साथ रखी फ़ाइलें`),
  keptFilesNone: draft("कोई फ़ाइल नहीं रखी गई"),
  keptFilesOpen: draft((name) => `${name} खोलें`),
  keptFilesKeptAt: draft((when) => `${when} को रखी गई`),
  keptFilesKeptBy: draft((when, who) => `${who} ने ${when} को रखी`),

  // runs/RunsList.tsx
  runsTitle: draft("हर रन, पुराना और चालू"),
  runsIntro: draft("हर वैल्यू स्ट्रीम रन खोजें और छाँटें। कोई रन खोलकर हर स्टेप और ठीक-ठीक क्या भेजा गया, देखें।"),
  runsShow: draft("दिखाएँ"),
  runsAll: draft("सभी"),
  runsLive: draft("चालू"),
  runsWaiting: draft("आपके इंतज़ार में"),
  runsFinished: draft("पूरे हुए"),
  runsStageFinished: draft("पूरा"),
  runsRunning: draft("चल रहा है"),
  runsLate: draft("देरी"),
  runsSearch: draft("रन खोजें"),
  runsWhat: draft("रन"),
  runsNoMatch: draft((q: string) => `“${q}” से कोई रन नहीं मिला।`),
  runsNone: draft("अभी यहाँ कोई रन नहीं है।"),
  runsRun: draft("रन"),
  runsValueStream: draft("वैल्यू स्ट्रीम"),
  runsStage: draft("चरण"),

  // runs/RunPanel.tsx
  runPanelMessage: draft("संदेश"),
  runPanelDocument: draft("दस्तावेज़"),
  runPanelPhoto: draft("फ़ोटो"),
  runPanelChallan: draft("चालान"),
  runPanelReceipt: draft("रसीद"),
  runPanelFile: draft("फ़ाइल"),
  runPanelArtefacts: draft("भेजा और मिला"),
  runPanelWhy: draft("क्यों?"),
  runPanelNoWhy: draft("ऑडिट लॉग में बस यह दर्ज है कि किसने किया; इसके पीछे कोई नियम, प्लेबुक लाइन या मंज़ूरी नहीं।"),
  runPanelThisRun: draft("इस रन"),
  runPanelUsedMiddle: draft("ने"),
  runPanelUsedAfter: draft(" इस्तेमाल किए।"),
  runPanelCredits: draft((credits: string) => `${credits} क्रेडिट`),
  runPanelUnplaced: draft((credits: string) => ` इनमें से ${credits} किसी स्टेप पर नहीं हैं।`),
  runPanelInvolved: draft("इसमें शामिल सभी"),
  runPanelCall: draft((name: string) => `${name} को कॉल करें`),
  runPanelAlsoInvolved: draft((names: string) => `ये भी शामिल: ${names}`),
  runPanelSteps: draft("स्टेप"),
  runPanelNoSteps: draft("ऑडिट लॉग में इस रन पर अभी कुछ नहीं है।"),
  runPanelTheRun: draft("रन"),
  runPanelStep: draft((node: string) => `स्टेप ${node}`),
  runPanelMessageTo: draft((party: string) => `${party} को संदेश`),
  runPanelMessageFrom: draft((party: string) => `${party} से संदेश`),
  runPanelSentOn: draft((channel: string) => `${channel} पर भेजा, ठीक जैसा भेजा गया:`),
  runPanelReceivedOn: draft((channel: string) => `${channel} पर मिला, ठीक जैसा मिला:`),
  runPanelForbidden: draft("आपके रोल में इस रन का पैनल देखने की अनुमति नहीं है।"),
  runPanelNotARun: draft("यह कोई रन नहीं है।"),
  runPanelFailed: draft("रन लोड नहीं हो सका। थोड़ी देर में फिर कोशिश करें।"),
  runPanelPartly: draft("इस रन का कुछ इतिहास या क्रेडिट्स लोड नहीं हो सके। थोड़ी देर में फिर कोशिश करें।"),
  runPanelLoading: draft("रन लोड हो रहा है…"),
  runStanding: draft("यह रन कहाँ है"),
  runStandingATask: draft("एक टास्क"),
  runStandingBlocked: draft((task: string) => `${task} पर रुका हुआ`),
  runStandingWaiting: draft((task: string) => `इंतज़ार: ${task}`),
  runStandingPrepared: draft((task: string) => `LAMP ने ${task} तैयार किया है; पुष्टि होने पर आगे बढ़ेगा`),
  runStandingPaused: draft("रुका हुआ"),
  runStandingHalted: draft("बंद: LAMP ने यह रन रोका और एक इंसिडेंट दर्ज किया"),
  runStandingPausedBecause: draft({
    credits_exhausted: "बिज़नेस के क्रेडिट ख़त्म हो गए हैं: टॉप-अप होने पर आगे बढ़ेगा।",
    kill_switch: "LAMP के एजेंट रुके हुए हैं: ओनर के फिर चालू करने पर आगे बढ़ेगा।",
    read_only: "बिज़नेस अभी केवल पढ़ने के लिए है।",
  }),
  runStandingOnYou: draft("यह आपका इंतज़ार कर रहा है।"),
  runStandingOnLamp: draft("LAMP इसे कर रहा है।"),
  runStandingOn: draft((role: string) => `यह ${role} का इंतज़ार कर रहा है।`),
  runStandingOpen: draft("खोलें"),
  runStepStatus: draft({
    open: "करना है",
    awaiting_approval: "पुष्टि का इंतज़ार",
    blocked: "रुका हुआ",
    done: "हो गया",
  }),
  runPanelCostHeading: draft("लागत और मार्जिन"),
  runPanelCostOrder: draft((number: string, customer: string) => `${customer} का सेल्स ऑर्डर ${number}`),
  runPanelCostCosts: draft("लागत"),
  runPanelCostEstimated: draft("अनुमान"),
  runPanelCostSoFar: draft("अब तक की लागत"),
  runPanelCostProjected: draft("अंत में होगी"),
  runPanelCostMaterial: draft("माल"),
  runPanelCostTime: draft("समय"),
  runPanelCostJobWork: draft("जॉब वर्क"),
  runPanelCostTotal: draft("कुल"),
  runPanelMarginQuoted: draft((pct: string) => `कोटेशन का मार्जिन: ${pct}`),
  runPanelMarginProjected: draft((pct: string) => `जिस मार्जिन की ओर जा रहा है: ${pct}`),
  runPanelMarginAtRisk: draft((points: number) => `कोटेशन के मार्जिन से ${points} पॉइंट से ज़्यादा नीचे।`),
  runPanelCostCauses: draft("योजना से ज़्यादा क्या लगा"),
  runPanelCostNoCauses: draft("BOM और रूटिंग से ज़्यादा कुछ बुक नहीं हुआ।"),

  // runs/panel.ts
  runPanelSomeone: draft("कोई"),
  runPanelAChannel: draft("किसी चैनल"),
  runPanelObjectDocument: draft("दस्तावेज़"),
  runPanelObjectInvoice: draft("इनवॉइस"),
  runPanelObjectDeliveryChallan: draft("डिलीवरी चालान"),
  runPanelObjectChallan: draft("चालान"),
  runPanelObjectReceipt: draft("रसीद"),
  runPanelObjectQuotation: draft("कोटेशन"),
  runPanelObjectSalesOrder: draft("सेल्स ऑर्डर"),
  runPanelObjectPurchaseOrder: draft("परचेज़ ऑर्डर"),
  runPanelObjectEnquiry: draft("पूछताछ"),
  runPanelObjectWorkOrder: draft("वर्क ऑर्डर"),
  runPanelWhoAgent: draft((who: string, work: string) => `${who}, LAMP के ${work} पर`),
  runPanelEnteredStep: draft((run: string, step: string) =>
    step ? `${run} अगले स्टेप ${step} पर गया` : `${run} आगे बढ़ा`,
  ),
  runPanelFileStarted: draft((name) => `LAMP को दी गई ${name} से यह शुरू हुआ`),
  runPanelFileKept: draft((name, words) => `${name} यहाँ रखी गई${words ? `: “${words}”` : ""}`),
  runPanelRunCompleted: draft((run: string) => `${run} पूरा हुआ`),
  runPanelRunBlocked: draft((run: string) => `${run} रुका और किसी का इंतज़ार कर रहा है`),
  runPanelRunPaused: draft((run: string) => `${run} रोका गया`),
  runPanelRunResumed: draft((run: string) => `${run} फिर आगे बढ़ा`),
  runPanelRunMigrated: draft((run: string) => `${run} बिज़नेस के नए तरीके पर गया`),
  runPanelNudged: draft(
    (role: string | undefined, run: string) => `${run} के देर वाले काम के लिए ${role ?? "रोल"} को याद दिलाया`,
  ),
  runPanelEscalated: draft((to: string | undefined, run: string) => `${run} ${to ?? "ऊपर किसी"} को आगे भेजा`),
  runPanelDeviationRaised: draft(
    (run: string, what: string | undefined) => `${run} को जैसा है वैसा आगे जाने देने को कहा${what ? `: ${what}` : ""}`,
  ),
  runPanelDeviationApproved: draft(
    (run: string, kind: string | undefined) => `${run} को जैसा है वैसा आगे जाने दिया${kind ? ` (${kind})` : ""}`,
  ),
  runPanelIssued: draft(
    (name: string, number: string | undefined, run: string | undefined) =>
      `${run ? `${run} के लिए ` : ""}${name}${number ? ` ${number}` : ""} जारी किया`,
  ),
  runPanelChecksPassed: draft("एजेंट का नतीजा जाँच में पास हुआ"),
  runPanelChecksFailed: draft("एजेंट का नतीजा जाँच में फ़ेल हुआ"),
  runPanelConfirmed: draft("एजेंट का तैयार काम पक्का किया"),
  runPanelCorrected: draft("एजेंट का तैयार काम सुधारा"),
  runPanelSentBack: draft("एजेंट का तैयार काम लौटाया"),
  runPanelUndid: draft((reason: string | undefined) => `एजेंट का काम पलटा${reason ? `: ${reason}` : ""}`),
  runPanelSentMessage: draft((party: string, channel: string | undefined) =>
    channel ? `${party} को ${channel} पर संदेश भेजा` : `${party} को संदेश भेजा`,
  ),
  runPanelReceivedMessage: draft((party: string, channel: string | undefined) =>
    channel ? `${party} से ${channel} पर संदेश मिला` : `${party} से संदेश मिला`,
  ),
  runPanelRule: draft((rule: string) => `नियम: ${rule}`),
  runPanelPlaybookLine: draft((line: string) => `प्लेबुक लाइन: ${line}`),
  runPanelGoesOn: draft((from: string | undefined) =>
    from ? `वैल्यू स्ट्रीम ${from} के बाद इस स्टेप पर आती है।` : "वैल्यू स्ट्रीम यहीं से शुरू होती है।",
  ),
  runPanelBlockedBecause: draft((reason: string) => `अटका क्योंकि ${reason}`),
  runPanelOverdue: draft((expected: string | undefined) => (expected ? `देरी: इसे ${expected} में होना था।` : "देरी।")),
  runPanelExpected: draft((expected: string) => `इसे ${expected} में होना था।`),
  runPanelApprovalRule: draft((approver: string) => `मंज़ूरी का नियम इसे ${approver} के पास मंज़ूरी के लिए भेजता है।`),
  runPanelApprovedBy: draft((by: string) => `${by} ने मंज़ूर किया।`),
  runPanelConfirmedBy: draft((who: string) => `${who} ने पक्का किया।`),

  // floor/Floor.tsx
  floorLate: draft("देरी से"),
  floorNeedsYou: draft("आपकी ज़रूरत"),
  floorLive: draft((live: number) => `${live} चालू`),
  floorNeedYou: draft((count: number) => `${count} को आपकी ज़रूरत`),
  floorLateCount: draft((late: number) => `${late} देरी से`),
  floorMoves: draft((driver: string) => `${driver} पर असर`),
  floorStream: draft((name: string) => STREAMS_HI[name] ?? name),
  floorNothing: draft("यहाँ कुछ नहीं"),
  floorFailed: draft("फ़्लोर लोड नहीं हो सका। थोड़ी देर में फिर कोशिश करें।"),
  floorLoading: draft("फ़्लोर लोड हो रहा है…"),
  floorTitle: draft("फ़्लोर, अभी"),
  floorIntro: draft(
    "हर वैल्यू स्ट्रीम एक कार्ड है: उसके स्टेप बाएँ से दाएँ, हर चालू रन अपने स्टेप पर। पीली किनारी वाला रन आपके इंतज़ार में है।",
  ),
  floorNone: draft("अभी कोई वैल्यू स्ट्रीम चालू नहीं है। अपने फ़ोकस एरिया चुनने के लिए LAMP सेट करें।"),

  // preview/Preview.tsx
  previewActive: draft("चालू"),
  previewCancelled: draft("रद्द"),
  previewSuspended: draft("निलंबित"),
  previewItem: draft("आइटम"),
  previewQuantity: draft("मात्रा"),
  previewUnitPrice: draft("दर"),
  previewAmount: draft("रकम"),
  previewOrderValue: draft("ऑर्डर का मूल्य"),
  previewNumber: draft("नंबर"),
  previewDate: draft("तारीख"),
  previewDeliveryBy: draft("डिलीवरी की तारीख"),
  previewCustomerPo: draft("ग्राहक का PO"),
  previewCustomerNotes: draft((customer: string) => `${customer} पर नोट`),
  previewBillTo: draft("बिल किसे"),
  previewCustomerGstin: draft((gstin: string) => `, GSTIN ${gstin}`),
  previewUnregistered: draft(", बिना GST पंजीकरण"),
  previewGstinCheck: draft("GSTIN जाँच"),
  previewGstinChecked: draft((status: string, on: string) => `GST पोर्टल पर ${status}, ${on} को जाँचा`),
  previewPlaceOfSupply: draft("सप्लाई की जगह"),
  previewIntraState: draft("राज्य के अंदर: CGST और SGST"),
  previewInterState: draft("दूसरे राज्य में: IGST"),
  previewFrom: draft("भेजने वाला"),
  previewSupplier: draft((name: string, gstin: string) => `${name}, GSTIN ${gstin}`),
  previewVehicle: draft("गाड़ी"),
  previewInvoiceLines: draft("इनवॉइस की लाइनें"),
  previewHsn: draft("HSN"),
  previewTaxableValue: draft("कर योग्य मूल्य"),
  previewGst: draft("GST"),
  previewCgst: draft("CGST"),
  previewSgst: draft("SGST"),
  previewIgst: draft("IGST"),
  previewRoundOff: draft("राउंड ऑफ़"),
  previewTotal: draft("कुल बिल"),
  previewAgainstInvoice: draft("इनवॉइस के सामने"),
  previewTotalCredit: draft("कुल क्रेडिट"),
  previewAdvance: draft("मिला एडवांस"),
  previewBalance: draft("बाकी रकम"),
  previewTo: draft("किसे"),
  previewThrough: draft("किससे"),
  previewOrder: draft("ऑर्डर"),
  previewCustomer: draft("ग्राहक"),
  previewDrawing: draft("ड्रॉइंग"),
  previewRevision: draft("संशोधन"),
  previewNoCustomer: draft("कोई ग्राहक दर्ज नहीं"),
  previewOpenDrawing: draft("ड्रॉइंग खोलें"),
  previewJournal: draft("जर्नल"),
  previewReversesOn: draft("कब उलटेगा"),
  previewEntries: draft("इसकी एंट्रियाँ"),
  previewAccount: draft("खाता"),
  previewDebit: draft("डेबिट"),
  previewCredit: draft("क्रेडिट"),
  previewWorking: draft("हिसाब"),
  previewOrdered: draft("ऑर्डर किया"),
  previewToPick: draft("स्टॉक से निकालकर देना है"),
  previewQuotation: draft("कोटेशन"),
  previewValidUntil: draft("कब तक मान्य"),
  previewQuoted: draft("कोट किया गया"),
  previewQuotationTotal: draft("कोटेशन का कुल"),
  previewMargin: draft("मार्जिन"),
  previewMarginPct: draft((pct: number) => `${pct}%`),
  previewCoverNote: draft("साथ का पत्र"),
  previewEnquiry: draft("पूछताछ"),
  previewAskedFor: draft("क्या माँगा"),
  previewQuotationBy: draft("कोटेशन का वादा"),

  // preview/ActionPreview.tsx
  previewNotSent: draft((name: string) => `नहीं भेजा। जब तक आप फिर न भेजें, ${name} को कुछ नहीं मिलेगा।`),
  previewMessageTo: draft((name: string) => `${name} को संदेश`),
  previewSendingIn: draft((name: string, seconds: number) => `${seconds} सेकंड में ${name} को भेज रहे हैं।`),
  previewUndo: draft("वापस लें"),
  emailUndone: draft((name: string) => `वापस लिया: ${name} को कोई ईमेल नहीं गया।`),
  emailAlreadySent: draft((name: string) => `वापस लेने में देर हो गई: ${name} को ईमेल चला गया है।`),
  previewSendTo: draft((name: string) => `${name} को भेजें`),
  previewCallInstead: draft((name: string) => `इसके बजाय ${name} को कॉल करें`),
  previewCantIssue: draft((problems: string) => `अभी जारी नहीं हो सकता: ${problems}।`),
  previewCantEdit: draft("जारी होने के बाद इसे बदला नहीं जा सकता।"),
  previewConfirmIssue: draft("पक्का करें और जारी करें"),
  previewConfirm: draft("पक्का करें"),

  // preview/Failure.tsx
  failureUnreachable: draft("LAMP तक पहुँच नहीं हो सकी, इसलिए कुछ नहीं हुआ।"),
  failureCheckConnection: draft("अपना इंटरनेट देखें और फिर कोशिश करें।"),
  failureSignedOut: draft("आप साइन आउट हो गए थे, इसलिए कुछ नहीं हुआ।"),
  failureSignIn: draft("फिर से साइन इन करें, फिर कोशिश करें।"),
  failureForbidden: draft("आपके रोल में इसकी अनुमति नहीं, इसलिए कुछ नहीं हुआ।"),
  failureAskOwner: draft("किसी मालिक से कहें कि वे करें, या आपको ऐसा रोल दें जो कर सके।"),
  failureChanged: draft("आपके देखते-देखते यह बदल गया, इसलिए कुछ नहीं हुआ।"),
  failureCheckAgain: draft("अब यह ताज़ा हालत में दिख रहा है: देखें और फिर कोशिश करें।"),
  failureNotFinished: draft("LAMP इसे पूरा नहीं कर सका।"),
  failureCheckHappened: draft("अब यह ताज़ा हालत में दिख रहा है: फिर कोशिश से पहले देखें कि यह हुआ या नहीं।"),
  failureRefused: draft((code: string) => `LAMP ने इसे मना किया (${code})।`),
  failureChange: draft("जो बताया गया है उसे बदलें और फिर कोशिश करें।"),
  failureRetry: draft("फिर कोशिश करें"),

  // kill-switch/KillSwitch.tsx
  killSwitchDefaultReason: draft("मालिक ने रोका"),
  killSwitchHeld: draft((held: number) =>
    held === 1 ? "1 संदेश भेजा जाना बाकी है।" : `${held} संदेश भेजे जाने बाकी हैं।`,
  ),
  killSwitchUnreachable: draft("LAMP तक पहुँच नहीं हो सकी। अपना इंटरनेट देखें और फिर कोशिश करें।"),
  killSwitchByOperations: draft("LAMP ऑपरेशंस ने एजेंट रोके हैं।"),
  killSwitchResuming: draft("फिर शुरू कर रहे हैं…"),
  killSwitchResume: draft("LAMP के एजेंट फिर शुरू करें"),
  killSwitchPause: draft("LAMP के एजेंट रोकें"),
  killSwitchExplain: draft(
    "LAMP के एजेंट तुरंत रुक जाते हैं। रन अपने अगले स्टेप पर रुकते हैं, और संदेश व फ़ाइलिंग तब तक रुकी रहती हैं जब तक आप फिर शुरू न करें। सब लोग सब कुछ पढ़ सकते हैं और उन्हें कारण बताया जाता है।",
  ),
  killSwitchWhy: draft("क्यों (यह सबको दिखेगा)"),
  killSwitchPausing: draft("रोक रहे हैं…"),
  killSwitchPauseNow: draft("अभी रोकें"),

  // The modal panel.
  dialogClose: draft("बंद करें"),

  stepUpTitle: draft("पक्का करें कि यह आप हैं"),
  stepUpBankDetails: draft("आप बैंक जानकारी बदलने जा रहे हैं: पैसा कहाँ जाता है।"),
  stepUpRoles: draft("आप बदलने जा रहे हैं कि किसी व्यक्ति के पास कौन-सी भूमिकाएँ हैं।"),
  stepUpOwner: draft("आप किसी को बिज़नेस का मालिक बनाने जा रहे हैं।"),
  stepUpExport: draft("आप बिज़नेस का सारा डेटा डाउनलोड करने जा रहे हैं।"),
  stepUpPayment: draft("आप एक भुगतान मंज़ूर करने जा रहे हैं।"),
  stepUpPinChange: draft("आप नया PIN सेट करने जा रहे हैं।"),
  stepUpOffboard: draft("आप बिज़नेस को LAMP से हटाने जा रहे हैं।"),
  stepUpWhy: draft("LAMP पहले पक्का करता है कि यह आप हैं। यह सिर्फ़ इसी एक काम के लिए है।"),
  stepUpSendCode: draft("मुझे कोड भेजें"),
  stepUpSent: draft("LAMP ने आपके Telegram पर, या वह न हो तो आपके ईमेल पर, कोड भेजा है। उसे यहाँ डालें।"),
  stepUpCode: draft("कोड"),
  stepUpConfirm: draft("पक्का करें"),
  stepUpSendAgain: draft("नया कोड भेजें"),
  stepUpOrPin: draft("या अपना PIN इस्तेमाल करें"),
  stepUpConfirmPin: draft("मेरे PIN से पक्का करें"),
  stepUpWrongCode: draft(
    "यह कोड गलत है, उसका समय निकल गया है या वह पहले ही इस्तेमाल हो चुका है। उसे जाँचें, या नया कोड भेजें।",
  ),
  stepUpWrongPin: draft("यह PIN गलत है। फिर कोशिश करें, या कोड इस्तेमाल करें।"),
  stepUpCodeRequired: draft("इसे सिर्फ़ कोड से पक्का किया जा सकता है, PIN से नहीं।"),
  stepUpTooManyCodes: draft("आपने बहुत सारे कोड माँगे हैं। कुछ मिनट रुकें, फिर नया कोड भेजें।"),
  stepUpTooManyTries: draft("बहुत बार गलत डाला गया। कुछ मिनट रुकें, फिर कोशिश करें।"),
  stepUpNoMethod: draft("LAMP के पास आपका कोई पक्का ईमेल या Telegram नहीं है, इसलिए वह कोड नहीं भेज सकता।"),
  stepUpSignedOut: draft("आप साइन आउट हो गए हैं। फिर से साइन इन करें, फिर से शुरू करें।"),
  stepUpClosed: draft("कुछ नहीं किया गया: पुष्टि पूरी होने से पहले बंद कर दी गई।"),
  ownerPagesRefused: draft((code: string) => `LAMP ने इसे मना किया (${code})`),
  ownerPagesUnreachable: draft("LAMP से जुड़ नहीं पाए। अपना कनेक्शन देखकर फिर कोशिश करें।"),

  memoryViaChat: draft("चैट से"),
  memoryViaWhatsapp: draft("WhatsApp से"),
  memoryViaEmail: draft("ईमेल से"),
  memoryViaPhone: draft("फ़ोन कॉल से"),
  memoryInPerson: draft("आमने-सामने"),
  memoryViaCockpit: draft("कॉकपिट से"),
  memoryVia: draft((channel: string) => `${channel} से`),
  memorySource: draft(
    (by: string, byLamp: boolean, day: string, where: string) =>
      `${by}${byLamp ? " (LAMP ने सेव किया)" : ""}, ${day}, ${where}`,
  ),
  memoryForget: draft("भूल जाएँ"),
  memoryForgetLabel: draft((what: string) => `${what} भूल जाएँ`),
  memoryForgetAsk: draft((what: string) => `${what} भूल जाएँ?`),
  memoryForgetWarning: draft("LAMP इसे हमेशा के लिए मिटा देगा।"),
  memoryForgetIt: draft("हाँ, भूल जाएँ"),
  memoryKeepIt: draft("रहने दें"),
  memoryAddNote: draft("नोट जोड़ें"),
  memoryNoteHint: draft("ऐसी बात जो LAMP खुद नहीं जान सकता, जैसे पसंदीदा भाषा"),
  memorySaveNote: draft("नोट सेव करें"),
  memoryAddRelationshipNote: draft("रिश्ते का नोट जोड़ें"),
  memoryRelationshipRules: draft(
    "सिर्फ़ शिष्टाचार के लिए: याद दिलाना, लहजा और सही समय। उधार, दाम, मंज़ूरी या प्राथमिकता तय करने में कभी इस्तेमाल नहीं होता। LAMP जाति, राजनीति, यौन जीवन, बायोमेट्रिक या जेनेटिक जानकारी या किसी की निजी आर्थिक स्थिति कभी नहीं रखता, और धर्म सिर्फ़ अभिवादन की पसंद के रूप में।",
  ),
  memoryContact: draft("संपर्क"),
  memoryKind: draft("प्रकार"),
  memoryNote: draft("नोट"),
  memorySaveRelationshipNote: draft("रिश्ते का नोट सेव करें"),
  memoryCategoryFamilyEvent: draft("पारिवारिक मौका"),
  memoryCategoryHealth: draft("सेहत (90 दिन तक)"),
  memoryCategoryFestivalGreeting: draft("त्योहार की शुभकामना"),
  memoryCategoryGreetingPreference: draft("अभिवादन की पसंद"),
  memoryCategoryCourtesy: draft("अन्य शिष्टाचार"),
  memoryNoBehaviour: draft("अभी तक कोई भुगतान नहीं, इसलिए भुगतान का रिकॉर्ड दिखाने को कुछ नहीं।"),
  memoryBehaviour: draft(
    (onTimePercent: number, paid: number, daysLate: number, overdue: number) =>
      `${String(paid)} में से ${String(onTimePercent)}% बार समय पर भुगतान; देर होने पर औसतन ${String(daysLate)} दिन देर; अभी ${String(overdue)} बकाया।`,
  ),
  memoryFromLedgers: draft("आपके लेजर से निकाला गया।"),
  memoryLoading: draft("LAMP की जानकारी लोड हो रही है…"),
  memoryForgotten: draft("भुला दिया।"),
  memoryKnowsAbout: draft((name: string) => `LAMP ${name} के बारे में क्या जानता है`),
  memoryBehaviourHeading: draft("लेन-देन का तरीका"),
  memoryNotes: draft("नोट"),
  memoryNoNotes: draft("अभी कोई नोट नहीं।"),
  memoryNoteSaved: draft("नोट सेव हो गया।"),
  memoryRelationshipNotes: draft("रिश्ते के नोट"),
  memoryNoRelationshipNotes: draft("रिश्ते का कोई नोट नहीं।"),
  memoryKeptUntil: draft((day: string) => `${day} तक रखा जाएगा।`),
  memoryRenewLabel: draft((text: string) => `"${text}" 90 दिन और रखें`),
  memoryRenew: draft("आगे बढ़ाएँ"),
  memoryRenewed: draft("90 दिन और रखा जाएगा।"),
  memoryRelationshipNoteSaved: draft("रिश्ते का नोट सेव हो गया।"),
  memoryAccepted: draft((headline: string) => `${headline} लागू करने के लिए सेटिंग्स में पक्का करें।`),
  memoryAcceptedPlain: draft("मान लिया।"),
  memoryDismissed: draft("हटा दिया। LAMP यह 30 दिन तक दोबारा नहीं सुझाएगा।"),
  memoryNoSuggestions: draft("इस हफ़्ते कोई सुझाव नहीं।"),
  memorySuggestions: draft("सुझाव"),
  memoryAcceptLabel: draft((title: string) => `मानें: ${title}`),
  memoryPreviewChange: draft("बदलाव देखें"),
  memoryTakeALook: draft("देखें"),
  memoryDismissLabel: draft((title: string) => `हटाएँ: ${title}`),
  memoryNotNow: draft("अभी नहीं"),
  memoryIntro: draft(
    'LAMP जो जानता है वह आपके रिकॉर्ड से निकाला गया है, किसने कहा उसके साथ नोट किया गया है, या आपके मानने के लिए सुझाया गया है। चैट में पूछें "आप … के बारे में क्या जानते हो?" या "यह भूल जाओ"।',
  ),
  memoryCounterparty: draft("पार्टी"),
  memoryChooseCounterparty: draft("पार्टी चुनें"),

  insightsPercentagePoints: draft((value: string) => `${value} प्रतिशत अंक`),
  insightsAmount: draft((value: string, unit: string, one: boolean) => `${value} ${unitHi(unit, one)}`),
  insightsPoints: draft((value: string) => `${value} अंक`),
  insightsNoFigureToCompare: draft((month: string) => `तुलना के लिए ${month} का आँकड़ा नहीं`),
  insightsSameAs: draft((month: string) => `${month} जितना ही`),
  insightsSlower: draft((by: string, month: string) => `${month} से ${by} धीमा`),
  insightsFaster: draft((by: string, month: string) => `${month} से ${by} तेज़`),
  insightsHigher: draft((by: string, month: string) => `${month} से ${by} ज़्यादा`),
  insightsLower: draft((by: string, month: string) => `${month} से ${by} कम`),
  insightsBetter: draft((moved: string) => `${moved}, यानी बेहतर`),
  insightsWorse: draft((moved: string) => `${moved}, यानी खराब`),
  insightsNoFigureYet: draft((month: string) => `${month} का आँकड़ा अभी नहीं`),
  insightsLastFigure: draft((month: string, value: string) => `। ${month}: ${value}`),
  insightsKpiName: draft((name: string) => KPIS_HI[name] ?? name),
  insightsReason: draft(reasonHi),
  insightsWhatMovedIt: draft("किससे बदला"),
  insightsNothingStoodOut: draft((month: string) => `${month} में कोई एक रिकॉर्ड खास नहीं रहा।`),
  insightsLoadFailed: draft("इनसाइट्स लोड नहीं हो पाए। थोड़ी देर में फिर कोशिश करें।"),
  insightsLoading: draft("इनसाइट्स लोड हो रहे हैं…"),
  insightsHeading: draft("आपका कारोबार कैसा चल रहा है"),
  insightsRange: draft((from: string, to: string) => `${from} से ${to}। `),
  insightsIntro: draft("हर वह चीज़ जिसे आप सुधारना चाहते हैं: उसके आँकड़े कैसे बदले और किस वजह से।"),

  // Expenses.
  expensesHeading: draft((label: string) => `${label} के हिसाब से खर्च`),
  expensesIntro: draft("महीने में खर्च, हर खर्च खाते के हिसाब से। किसी आँकड़े को खोलकर उसके पीछे की एंट्री देखें।"),
  expensesMonth: draft("महीना"),
  expensesSplitBy: draft("किसके हिसाब से"),
  expensesDownload: draft("शीट के रूप में डाउनलोड करें"),
  expensesLoading: draft("खर्च लोड हो रहे हैं…"),
  expensesLoadFailed: draft("खर्च की रिपोर्ट लोड नहीं हो सकी। थोड़ी देर बाद फिर कोशिश करें।"),
  expensesNothing: draft((from: string, to: string) => `${from} से ${to} तक कोई खर्च दर्ज नहीं हुआ।`),
  expensesPayrollHidden: draft("पेरोल शामिल नहीं है: आपके रोल उसे नहीं दिखाते।"),
  expensesAccount: draft("खाता"),
  expensesTotal: draft("कुल"),
  expensesUnallocated: draft("किसी को नहीं सौंपा"),
  expensesFigure: draft((row: string, column: string) => `${row}, ${column}`),
  expensesDate: draft("तारीख"),
  expensesDocument: draft("दस्तावेज़"),
  expensesSupplier: draft("सप्लायर"),
  expensesAmount: draft("रकम"),
  expensesKind: draft("प्रकार"),
  expensesDocumentHeading: draft("दस्तावेज़"),
  expensesDocumentRefused: draft("वह दस्तावेज़ नहीं खुल सका।"),
  expensesClose: draft("बंद करें"),
  billsIntro: draft("खर्च के लिए हर सप्लायर का बिल: वह कहाँ है, किसका इंतज़ार कर रहा है और किससे जुड़ा है।"),
  billsAdd: draft("बिल जोड़ें"),
  billsAdding: draft("बिल पढ़ा जा रहा है…"),
  billsAddWords: draft("यह बिल दर्ज करें"),
  billsTryAgain: draft("फिर से कोशिश करें"),
  billsEnterByHand: draft("हाथ से दर्ज करें"),
  billsNotABill: draft("यह बिल नहीं है"),
  billsNotABillHint: draft("बिल नहीं है? यहाँ बताएँ, फिर Chat में LAMP को बताएँ कि यह क्या है।"),
  billsTyped: draft((name: string) => `${name} का बिल हाथ से दर्ज करें`),
  billsTypedSupplier: draft("सप्लायर, जैसा रिकॉर्ड में है"),
  billsTypedNumber: draft("बिल नंबर"),
  billsTypedDate: draft("बिल की तारीख"),
  billsTypedFor: draft("यह किसके लिए है"),
  billsTypedAmount: draft("टैक्स से पहले की रकम (₹)"),
  billsTypedGst: draft("GST (₹)"),
  billsTypedTotal: draft("कुल (₹)"),
  billsTypedDraft: draft("बिल का ड्राफ़्ट बनाएँ"),
  billsTypedCancel: draft("रद्द करें"),
  billsTypedNotRupees: draft((field: string) => `${field}: इसे रुपयों में बताएँ, जैसे 1180.50`),
  billsTypedAddsUp: draft("टैक्स से पहले की रकम और GST मिलकर कुल के बराबर होने चाहिए।"),
  billsFilters: draft("बिल छाँटें"),
  billsStandingFilter: draft("स्थिति"),
  billsAnyStanding: draft("कोई भी स्थिति"),
  billsSupplierFilter: draft("सप्लायर"),
  billsAnySupplier: draft("कोई भी सप्लायर"),
  billsValueFilter: draft("किससे जुड़ा"),
  billsAnyValue: draft("कुछ भी"),
  billsFrom: draft("से"),
  billsTo: draft("तक"),
  billsToConfirm: draft("पक्का होने का इंतज़ार"),
  billsToApprove: draft("मंज़ूरी का इंतज़ार"),
  billsToConfirmTax: draft("टैक्स का इंतज़ार"),
  billsUnpaid: draft("मंज़ूर, भुगतान बाकी"),
  billsPaid: draft("भुगतान हो गया"),
  billsHeld: draft("रुका हुआ"),
  billsDate: draft("तारीख"),
  billsSupplier: draft("सप्लायर"),
  billsNumber: draft("बिल"),
  billsTotal: draft("कुल"),
  billsTds: draft("TDS"),
  billsPayable: draft("देय"),
  billsStatus: draft("स्थिति"),
  billsWaitsOn: draft("किसका इंतज़ार"),
  billsYou: draft("आप"),
  billsBelongsTo: draft("किससे जुड़ा"),
  billsOpen: draft((number: string, supplier: string) => `${supplier} का बिल ${number} खोलें`),
  billsTotals: draft((count: number) => `पोस्ट हुए: इन फ़िल्टरों में आए ${count} स्वीकृत या चुकाए गए बिल`),
  billsWaitingTotal: draft((count: number) => `अभी पोस्ट नहीं हुए: पुष्टि या स्वीकृति की प्रतीक्षा में ${count} बिल`),
  billsInNeither: draft(
    (count: number) => `रोके गए, या LAMP द्वारा अस्वीकार किए गए ${count} बिल किसी भी कुल में नहीं हैं।`,
  ),
  billsAmountsHidden: draft("रकम नहीं दिखाई गई: आपकी भूमिकाएँ लागत नहीं दिखातीं।"),
  billsEmpty: draft(
    "अभी कोई बिल नहीं। पहला बिल जोड़ने के लिए “बिल जोड़ें” दबाएँ और सप्लायर का बिल PDF या फ़ोटो के रूप में चुनें, या LAMP से चैट में भेजें।",
  ),
  billsNoneMatch: draft("इन शर्तों से कोई बिल नहीं मिला।"),
  billsLoading: draft("बिल लोड हो रहे हैं…"),
  billsLoadFailed: draft("बिल लोड नहीं हो सके। थोड़ी देर में फिर कोशिश करें।"),
  billsPage: draft((page: number, pages: number) => `पेज ${page} / ${pages}`),
  billsPrevious: draft("पिछला पेज"),
  billsNext: draft("अगला पेज"),
  billsLines: draft("लाइनें"),
  billsDescription: draft("विवरण"),
  billsAccount: draft("खर्च खाता"),
  billsAmount: draft("रकम"),
  billsSplit: draft("बँटवारा"),
  billsTax: draft("GST"),
  billsHeldBecause: draft((reason: string) => `रुका हुआ: ${reason}`),
  billsFile: draft("बिल"),
  billsOpenFile: draft((name: string) => `${name} खोलें`),
  billsPostings: draft("लेजर में पोस्ट हुआ"),
  billsNotPosted: draft("अभी पोस्ट नहीं हुआ: मंज़ूर होने पर बिल पोस्ट होता है।"),
  billsOpenRun: draft("इसका Run खोलें"),
  billsClose: draft("बंद करें"),
  insightsEmpty: draft("अभी दिखाने को कुछ नहीं। फ़ोकस एरिया सेट होने पर इनसाइट्स दिखेंगे।"),
  trendCaption: draft((name: string) => `${name}, महीने के हिसाब से`),
  trendMonth: draft("महीना"),
  trendNoFigure: draft("आँकड़ा नहीं"),

  // Plan and billing
  billingLoading: draft("प्लान और बिलिंग लोड हो रहा है…"),
  billingIntro: draft(
    "आपका प्लान, उसमें क्या शामिल है और आपने क्या भुगतान किया। आप Razorpay के अपने पेज पर भुगतान करते हैं: LAMP आपके कार्ड या बैंक का विवरण कभी नहीं देखता।",
  ),
  billingNoPayments: draft("यह LAMP इंस्टॉलेशन अभी भुगतान नहीं लेता।"),
  billingYourPlan: draft("आपका प्लान"),
  billingNoPlan: draft("अभी कोई पेड प्लान नहीं।"),
  billingPlanLine: draft((name: string, annual: boolean) => `${name}, ${annual ? "सालाना" : "मासिक"} भुगतान`),
  billingPaidFor: draft((from: string, to: string) => `${from} से ${to} तक का भुगतान हो चुका है।`),
  billingNextCharge: draft((date: string) => `अगला भुगतान ${date} को, आपके UPI ऑटोपे या कार्ड मैंडेट से।`),
  billingChargeFailed: draft(
    "पिछला भुगतान नहीं हो सका। Razorpay फिर से कोशिश करेगा: अपना UPI ऑटोपे या कार्ड मैंडेट जाँचें।",
  ),
  billingHalted: draft("Razorpay ने भुगतान की कोशिश रोक दी। LAMP इस्तेमाल करते रहने के लिए फिर से सब्सक्राइब करें।"),
  billingCancelledStatus: draft("रद्द: अब कुछ और नहीं लिया जाएगा।"),
  billingOverdue: draft(
    (since: string) =>
      `LAMP की फ़ीस ${since} से बकाया है। बिज़नेस चलता रहे, इसके लिए भुगतान करें: 22 दिन बकाया रहने पर यह केवल-पढ़ने वाला हो जाता है।`,
  ),
  billingReadOnly: draft(
    "LAMP की फ़ीस चुकाए जाने तक बिज़नेस केवल-पढ़ने वाला है। आप अब भी सब कुछ देख और एक्सपोर्ट कर सकते हैं।",
  ),
  billingThisMonth: draft("इस महीने"),
  billingPeople: draft((active: string, included: string) => `${included} में से ${active} लोग सक्रिय`),
  billingRegistrations: draft((count: string, included: string) => `${included} में से ${count} GST रजिस्ट्रेशन`),
  billingCreditsLine: draft(
    (used: string, included: string, bought: string) =>
      `${used} क्रेडिट इस्तेमाल हुए, प्लान के ${included} और खरीदे गए ${bought} में से`,
  ),
  billingExtraPeople: draft(
    (count: string, month: string, price: string) =>
      `${month} में ${count} अतिरिक्त सक्रिय लोग, ${price} प्रति व्यक्ति और GST, अगले भुगतान में`,
  ),
  billingHistory: draft("भुगतान का इतिहास"),
  billingNoHistory: draft("अभी कोई भुगतान नहीं।"),
  billingWhen: draft("तारीख़"),
  billingWhat: draft("किसके लिए"),
  billingAmount: draft("राशि"),
  billingReference: draft("Razorpay संदर्भ"),
  billingFailedCharge: draft("भुगतान विफल"),
  billingChoosePlan: draft("प्लान चुनें"),
  billingPlanOffer: draft(
    (name: string, people: string, credits: string) => `${name}: ${people} लोग, हर महीने ${credits} क्रेडिट`,
  ),
  billingMonthly: draft((total: string) => `मासिक, GST सहित ${total}`),
  billingAnnual: draft((total: string) => `सालाना, GST सहित ${total}`),
  billingOnlyHolder: draft("केवल अकाउंट होल्डर ही प्लान सब्सक्राइब या रद्द करता है।"),
  billingBuyCredits: draft("क्रेडिट खरीदें"),
  billingPackOffer: draft((credits: string, total: string) => `GST सहित ${total} में ${credits} क्रेडिट`),
  billingConfirm: draft("राशि की पुष्टि करें"),
  billingPrice: draft("कीमत"),
  billingGst: draft("18% GST"),
  billingTotal: draft("कुल"),
  billingPay: draft((total: string) => `Razorpay पर ${total} चुकाएँ`),
  billingBack: draft("वापस"),
  billingPaid: draft("भुगतान हो गया। धन्यवाद।"),
  billingCancelPlan: draft("प्लान रद्द करें"),
  billingCancelQuestion: draft(
    "प्लान रद्द करें? अब कुछ और नहीं लिया जाएगा, और जिस अवधि का भुगतान हो चुका है वह पूरी चलेगी।",
  ),
  billingCancelYes: draft("हाँ, प्लान रद्द करें"),
  billingCancelled: draft((until: string | undefined) =>
    until === undefined ? "रद्द हो गया।" : `रद्द हो गया। आपका प्लान ${until} तक चलेगा।`,
  ),
  billingInvoices: draft("टैक्स इनवॉइस"),
  billingNoInvoices: draft(
    "अभी कोई टैक्स इनवॉइस नहीं। हर भुगतान का LAMP का इनवॉइस यहाँ दिखेगा, और आपको ईमेल किया जाएगा।",
  ),
  billingInvoiceNumber: draft("इनवॉइस"),
  billingInvoicePdf: draft("PDF"),
  billingInvoiceOpen: draft((number: string) => `${number} खोलें`),
  billingInvoiceTo: draft("इनवॉइस इनके नाम बनते हैं"),
  billingInvoiceToNone: draft("अभी चुना नहीं गया: नीचे चुनें, वरना LAMP आपके इनवॉइस जारी नहीं कर सकता।"),
  billingInvoiceToLine: draft(
    (name: string, address: string, gstin: string | null) =>
      `${name}, ${address}${gstin ? `, GSTIN ${gstin}` : ", बिना GSTIN"}`,
  ),
  billingInvoiceToChange: draft("चुनें कि LAMP के इनवॉइस पर क्या हो"),
  billingInvoiceToHolder: draft("केवल अकाउंट होल्डर चुनते हैं कि LAMP के इनवॉइस पर क्या हो।"),
  billingInvoiceToRegistration: draft("बिल किया गया GSTIN"),
  billingInvoiceToNoGstin: draft("कोई नहीं: बिना GSTIN का इनवॉइस"),
  billingInvoiceToName: draft("कानूनी नाम"),
  billingInvoiceToAddress: draft("पता"),
  billingInvoiceToState: draft("राज्य कोड (दो अंक)"),
  billingInvoiceToSave: draft("सहेजें"),
  billingInvoiceToSaved: draft("सहेजा गया। LAMP के अगले इनवॉइस पर यही विवरण होंगे।"),
  billingGstinNeedsCorrecting: draft(
    (gstin: string) =>
      `GSTIN ${gstin} को सुधारना है: उसका आख़िरी अक्षर जाँच अक्षर नहीं है, इसलिए वह गलत टाइप हुआ है। सुधारे जाने तक LAMP के इनवॉइस पर वह नहीं होगा।`,
  ),
  billingGstinCorrectIn: draft("Settings में “इनवॉइस पर आपका बिज़नेस” के अंतर्गत इसे सुधारें"),

  creditsPack: draft((credits: string, price: string) => `${credits} क्रेडिट ${price} में`),
  creditsConfirmTopUp: draft("टॉप-अप पक्का करें"),
  creditsPayQuestion: draft((credits: string): readonly [string, string] => [
    `इस महीने ${credits} क्रेडिट के लिए `,
    " + 18% GST चुकाएँ?",
  ]),
  creditsPay: draft((price: string) => `${price} चुकाएँ`),
  creditsCancel: draft("रद्द करें"),
  creditsBuy: draft((pack: string) => `खरीदें: ${pack}`),
  creditsAutoRecharge: draft("इस महीने के 95% क्रेडिट खर्च होने पर अपने-आप टॉप-अप करें, ताकि रन कभी न रुकें"),
  creditsPackToBuy: draft("कौन सा पैक खरीदें"),
  creditsLoading: draft("क्रेडिट लोड हो रहे हैं…"),
  creditsIntro: draft("रन के हर स्टेप पर 0.1 क्रेडिट लगता है; एजेंट के हर तरह के काम का अपना तय दाम है।"),
  creditsLeft: draft((budget: string, included: string, bought: string | undefined): readonly [string, string] => [
    `${budget} में से `,
    ` क्रेडिट बाकी (${included} प्लान में${bought === undefined ? "" : `, ${bought} खरीदे`})।`,
  ]),
  creditsUsedThisMonth: draft("इस महीने खर्च हुए क्रेडिट"),
  creditsUsedPercent: draft((percent: number) => `${String(percent)}% खर्च`),
  creditsRunOut: draft(
    "आपके क्रेडिट खत्म हो गए हैं। टॉप-अप होने तक रन अपने अगले स्टेप पर रुके रहेंगे; सब लोग फिर भी देख सकते हैं और हाथ से काम कर सकते हैं।",
  ),
  creditsByKind: draft("काम के प्रकार के हिसाब से"),
  creditsNothingUsed: draft("इस महीने अभी तक कोई क्रेडिट खर्च नहीं हुआ।"),
  creditsKindOfTask: draft("काम का प्रकार"),
  creditsModelTier: draft("मॉडल टियर"),
  creditsTimes: draft("कितनी बार"),
  creditsCredits: draft("क्रेडिट"),
  creditsNoModel: draft("कोई मॉडल नहीं"),
  creditsTopUp: draft("टॉप-अप"),
  creditsAdded: draft((credits: string) => `इस महीने में ${credits} क्रेडिट जोड़े गए।`),
  creditsAutoRechargeOff: draft("ऑटो-रिचार्ज बंद है।"),
  creditsAutoRechargeOn: draft("ऑटो-रिचार्ज चालू है।"),
  creditsTopUps: draft("इस महीने के टॉप-अप"),
  creditsAutomatic: draft(" (ऑटो-रिचार्ज)"),
  creditsNoAnswerToday: draft("आज LAMP के मॉडल से कोई उत्तर नहीं मिला"),
  creditsNoAnswerCounts: draft(
    (free: string, charged: string, credits: string, perDay: string) =>
      `${free} मुफ़्त, LAMP के ख़र्च पर; ${charged} में ${credits} Credits लगे। दिन के पहले ${perDay} मुफ़्त हैं।`,
  ),

  auditCsvColumns: draft([
    "क्रम",
    "कब",
    "क्या",
    "किसने",
    "रन",
    "स्टेप",
    "बिज़नेस ऑब्जेक्ट",
    "कार्रवाई",
    "व्यक्ति id",
    "एजेंट टास्क क्लास",
    "रन id",
    "नोड",
    "बिज़नेस ऑब्जेक्ट id",
  ]),
  auditOpenRun: draft("रन खोलें"),
  auditTheRun: draft("रन"),
  auditRunWith: draft((name: string) => `${name} वाला रन`),
  auditAPerson: draft("एक व्यक्ति"),
  auditLampDoing: draft((work: string) => `LAMP (${work})`),
  auditRunHalted: draft((run: string) => `${run} एक घटना की वजह से रोका गया`),
  auditIncidentRaised: draft((run: string) => `${run} पर एक घटना दर्ज की`),
  auditIncidentResolved: draft((run: string) => `${run} की घटना सुलझाई`),
  auditDeviationDeclined: draft(
    (run: string, reason: string | undefined) => `${run} को आगे नहीं जाने दिया${reason ? `: ${reason}` : ""}`,
  ),
  auditSuggestionRaised: draft((run: string) => `${run} जैसे ऑर्डर आगे जाने देने का नियम बनाने का सुझाव दिया`),
  auditAgentStepTaken: draft((run: string) => `${run} पर काम तैयार किया`),
  auditJudgeSampled: draft((run: string) => `${run} पर अपना काम दूसरे मॉडल से जँचवाया`),
  auditRunStarted: draft((run: string) => `${run} पर काम शुरू किया`),
  auditTaskOpened: draft((task: string, run: string) => `${run} पर “${task}” खोला`),
  auditTaskConfirmed: draft(
    (task: string, run: string, edited: boolean) =>
      `${run} पर “${task}” ${edited ? "सुधार कर पक्का किया" : "पक्का किया"}`,
  ),
  auditTaskSentBack: draft(
    (task: string, run: string, reason: string | undefined) =>
      `${run} पर “${task}” लौटाया${reason ? `: ${reason}` : ""}`,
  ),
  auditTaskDone: draft((task: string, run: string) => `${run} पर “${task}” पूरा किया`),
  auditHoldReleased: draft(
    (run: string, reason: string | undefined) => `${run} को रोक से छोड़ा${reason ? `: ${reason}` : ""}`,
  ),
  auditPosted: draft(
    (name: string, number: string | undefined, run: string | undefined) =>
      `${run ? `${run} के लिए ` : ""}${name}${number ? ` ${number}` : ""} खातों में दर्ज किया`,
  ),
  auditATask: draft("एक काम"),
  auditActions: draft(AUDIT_ACTIONS),
  auditRun: draft("रन"),
  auditBusinessObject: draft("बिज़नेस ऑब्जेक्ट"),
  auditTheBusiness: draft("यह बिज़नेस"),
  auditPerson: draft("व्यक्ति (या सिस्टम)"),
  auditAgent: draft("एजेंट का काम"),
  auditFrom: draft("से"),
  auditTo: draft("तक"),
  auditIntro: draft("आपके कारोबार में जो कुछ भी हुआ, किसी व्यक्ति ने किया या LAMP ने, सबसे पुराना पहले।"),
  auditFilterLabel: draft("ऑडिट लॉग छाँटें"),
  auditFilter: draft("छाँटें"),
  auditExport: draft("CSV डाउनलोड करें"),
  auditLoading: draft("ऑडिट लॉग लोड हो रहा है…"),
  auditNoEntries: draft("कोई एंट्री मेल नहीं खाती।"),
  auditWhen: draft("कब"),
  auditWhat: draft("क्या"),
  auditWho: draft("किसने"),
  auditShowMore: draft("और दिखाएँ"),

  usersNever: draft("कभी नहीं"),
  usersName: draft("नाम"),
  usersEmail: draft("ईमेल"),
  usersMobile: draft("मोबाइल नंबर"),
  usersInviteBy: draft("किससे बुलाएँ"),
  usersRoles: draft("भूमिकाएँ"),
  usersInvite: draft("किसी को बुलाएँ"),
  usersThisPerson: draft("इस व्यक्ति"),
  usersThatPerson: draft("उस व्यक्ति"),
  usersChangeRolesOf: draft((who: string) => `${who} की भूमिकाएँ बदलें`),
  usersChange: draft("बदलें"),
  usersRolesOf: draft((who: string) => `${who} की भूमिकाएँ`),
  usersSaveRoles: draft("भूमिकाएँ सेव करें"),
  usersCancel: draft("रद्द करें"),
  usersLoading: draft("यूज़र लोड हो रहे हैं…"),
  usersEveryone: draft("सभी"),
  usersActive: draft("सक्रिय"),
  usersInvited: draft("बुलाए गए"),
  usersIntro: draft(
    "आपके कारोबार में LAMP के साथ काम करने वाले सभी लोग: वे कैसे साइन इन करते हैं, LAMP से कहाँ बात करते हैं, और क्या कर सकते हैं।",
  ),
  usersShow: draft("दिखाएँ"),
  usersNoOneHolds: draft("ये भूमिकाएँ अभी किसी के पास नहीं हैं: इनका इंतज़ार करने वाला काम आपके पास आता है"),
  usersRoleWaiting: draft(
    (role: string, waiting: number) => `${role}: ${waiting === 0 ? "कुछ इंतज़ार में नहीं" : `${waiting} इंतज़ार में`}`,
  ),
  usersInvitedNotice: draft(
    (email: string) =>
      `${email} को बुलाया। उन्हें एक ईमेल मिलेगा जिसके लिंक से वे साइन इन करेंगे; यह एक बार, 7 दिनों तक चलेगा।`,
  ),
  usersInvitedMobileNotice: draft(
    (who: string, mobile: string, code: string) =>
      `${who} को बुलाया। उनसे कहें कि ${mobile} वाले फ़ोन पर LAMP का Telegram बॉट खोलें और /start ${code} भेजें।`,
  ),
  usersRolesChanged: draft((who: string) => `${who} की भूमिकाएँ बदल दीं।`),
  usersPerson: draft("व्यक्ति"),
  usersRole: draft("भूमिका"),
  usersTalksOn: draft("LAMP से बात कहाँ"),
  usersSignsInWith: draft("साइन इन कैसे"),
  usersLastActive: draft("आखिरी बार सक्रिय"),
  usersStatus: draft("स्थिति"),
  usersAccountHolder: draft("खाताधारक"),
  usersInviteSent: draft("न्योता भेजा"),
  usersSendAgain: draft("न्योता फिर से भेजें"),
  usersSendAgainTo: draft((who: string) => `${who} को न्योता फिर से भेजें`),
  usersSentAgain: draft((who: string) => `${who} को नया न्योता भेजा। पिछला अब काम नहीं करेगा।`),
  usersSentAgainMobile: draft(
    (who: string, code: string) =>
      `${who} के लिए नया न्योता बनाया; पिछला अब काम नहीं करेगा। उनसे कहें कि अपने नंबर वाले फ़ोन पर LAMP का Telegram बॉट खोलें और /start ${code} भेजें।`,
  ),
  usersWhenInvited: draft("जब किसी को बुलाया जाता है"),
  usersWhenInvitedText: draft(
    "वे LAMP के ईमेल किए कोड से साइन इन करते हैं, और सिर्फ़ वही देखते हैं जो उनकी भूमिकाएँ अनुमति देती हैं। एक व्यक्ति की कई भूमिकाएँ हो सकती हैं, और कई लोग मालिक हो सकते हैं; एक मालिक खाताधारक होता है, जो सब्सक्रिप्शन संभालता है।",
  ),

  connectionsLoading: draft("कनेक्शन लोड हो रहे हैं…"),
  connectionsIntro: draft(
    "LAMP से जुड़े AI सहायक, जैसे Claude या ChatGPT। हर एक उसी व्यक्ति की तरह काम करता है जिसने उसे जोड़ा, उनकी भूमिकाओं से ज़्यादा कभी नहीं, और जो कुछ भी वह करने को कहता है वह उस व्यक्ति के LAMP में पुष्टि करने तक रुका रहता है। किसी को हटाएँ तो उसकी अगली कॉल मना हो जाती है।",
  ),
  connectionsNone: draft("कोई सहायक जुड़ा नहीं है।"),
  connectionsPerson: draft("व्यक्ति"),
  connectionsAssistant: draft("सहायक"),
  connectionsHost: draft("कहाँ से साइन इन"),
  connectionsMay: draft("क्या कर सकता है"),
  connectionsConnected: draft("जुड़ा"),
  connectionsLastUsed: draft("आखिरी इस्तेमाल"),
  connectionsRead: draft("पढ़ना"),
  connectionsReadAndAsk: draft("पढ़ना, और काम करने को कहना"),
  connectionsYou: draft("आप"),
  connectionsRevoke: draft("हटाएँ"),
  connectionsRevokeOf: draft((assistant: string, who: string) => `${who} के लिए ${assistant} हटाएँ`),
  connectionsRevoked: draft((assistant: string) => `${assistant} हटा दिया। LAMP को उसकी अगली कॉल मना होगी।`),

  rolesApprove: draft("मंज़ूरी"),
  rolesAct: draft("काम"),
  rolesSee: draft("देखना"),
  rolesCostsAndMargins: draft("लागत और मार्जिन"),
  rolesPayroll: draft("वेतन"),
  rolesBankAndCash: draft("बैंक और नकद बैलेंस"),
  rolesCustomerPricing: draft("ग्राहक के दाम"),
  rolesLoading: draft("भूमिकाएँ लोड हो रही हैं…"),
  rolesIntro: draft(
    "आपके कारोबार में हर भूमिका क्या देख, कर और मंज़ूर कर सकती है। चैट करके बदलें, जैसे “Floor Supervisor को Goods Receipt दिखाओ” या “Office Manager को लागत और मार्जिन दिखाओ”।",
  ),
  rolesWhatEachMayDo: draft("हर भूमिका क्या कर सकती है"),
  rolesArea: draft("क्षेत्र"),
  rolesSensitiveData: draft("संवेदनशील जानकारी"),
  rolesSensitiveDataEach: draft("हर भूमिका कौन सी संवेदनशील जानकारी देखती है"),
  rolesKindOfData: draft("जानकारी का प्रकार"),
  rolesYes: draft("हाँ"),
  rolesNo: draft("नहीं"),
  rolesHide: draft("छिपाएँ"),
  rolesShow: draft("दिखाएँ"),
  rolesHideFrom: draft((data: string, role: string) => `${role} से ${data} छिपाएँ`),
  rolesShowTo: draft((data: string, role: string) => `${role} को ${data} दिखाएँ`),
  rolesWhoApproves: draft("कौन क्या मंज़ूर करता है"),
  rolesWhen: draft("कब"),
  rolesGoesTo: draft("किसके पास जाता है"),
  rolesVersion: draft((version: string) => `बिज़नेस ट्री वर्ज़न ${version}।`),

  onboardingMostImportant: draft("सबसे ज़रूरी"),
  onboardingThen: draft("दूसरा"),
  onboardingOptional: draft("तीसरा (चाहें तो)"),
  onboardingQuestion: draft("सेटअप का सवाल"),
  onboardingPrompt: draft(
    (question: OnboardingQuestion, gstin: string | undefined): string =>
      ONBOARDING_PROMPTS[question.id]?.(gstin, question) ?? question.prompt,
  ),
  onboardingOption: draft(
    (question: string, option: { readonly id: string; readonly label: string }): string =>
      ONBOARDING_OPTIONS[question]?.[option.id] ?? option.label,
  ),
  onboardingChoose: draft("चुनें…"),
  onboardingGstin: draft("GSTIN"),
  onboardingLegalName: draft("रजिस्टर्ड कारोबार का नाम"),
  onboardingAddress: draft("रजिस्टर्ड पता"),
  onboardingState: draft("पते का राज्य कोड (दो अंक, जिनसे GSTIN शुरू होता है)"),
  onboardingSignatory: draft("अधिकृत हस्ताक्षरकर्ता"),
  onboardingInvoicesCarry: draft(
    (name: string, address: string, gstin: string, signatory: string) =>
      `आपके इनवॉइस पर छपेगा: ${name}, ${address}, GSTIN ${gstin}, हस्ताक्षर ${signatory}। इन्हें आप सेटिंग्स में बदल सकते हैं।`,
  ),
  onboardingNext: draft("आगे"),
  onboardingNames: draft("हर लाइन में एक नाम। अभी कुछ न जोड़ना हो तो खाली छोड़ दें।"),
  onboardingAdds: draft(
    (label: string, names: readonly string[]) => `${label} में LAMP ये जोड़ेगा: ${names.join(", ")}।`,
  ),
  onboardingReady: draft("चालू करने को तैयार"),
  onboardingTemplate: draft(
    (vertical: string | undefined) =>
      `LAMP आपका कारोबार${vertical === undefined ? "" : ` ${vertical} के लिए`} सेट करेगा और ये चालू करेगा:`,
  ),
  onboardingVertical: draft((id: string): string | undefined =>
    id === "mto" ? "ऑर्डर पर बनने वाले सामान की मैन्युफ़ैक्चरिंग" : undefined,
  ),
  onboardingProfile: draft((key: string, value: string): string | undefined => PROFILE_WORDS[key]?.[value]),
  onboardingFromAnswers: draft(
    (answers: string, trialDays: number) =>
      `आपके जवाबों से: ${answers}। आपका मुफ़्त ट्रायल ${String(trialDays)} दिन चलेगा; भुगतान की कोई जानकारी नहीं चाहिए।`,
  ),
  onboardingOff: draft(
    (names: readonly string[]) =>
      `अभी बंद: ${names.join(", ")}। इनमें से किसी को भी बाद में चैट में कहकर चालू कर सकते हैं, जैसे "switch on ${names[0] ?? "Kaizen Loop"}"।`,
  ),
  onboardingWhileOff: draft(
    (id: string, name: string): string | undefined =>
      ({
        VS22: `जब तक ${name} बंद है, मार्च में कुछ शुरू नहीं होगा: साल के आख़िरी दिन स्टॉक की गिनती नहीं, और साल के खाते बंद करने या जो ख़त्म हो रहा है उसे नवीनीकृत करने की याद नहीं।`,
        VS19: `जब तक ${name} बंद है, पेरोल के लिए किसी महीने की हाज़िरी लॉक नहीं होगी।`,
        VS21: `जब तक ${name} बंद है, अकाउंटेंट को तिमाही रिटर्न और अग्रिम कर की याद नहीं दिलाई जाएगी।`,
        VS5: `जब तक ${name} बंद है, ऑर्डर बिना उत्पादन योजना या जॉब कार्ड के वर्क ऑर्डर से बनेंगे।`,
      })[id],
  ),
  onboardingSwitchOn: draft("चालू करें"),
  onboardingSetUp: draft("LAMP सेट हो गया है।"),
  onboardingTrialUntil: draft((day: string) => ` आपका मुफ़्त ट्रायल ${day} तक चलेगा।`),
  onboardingWaitlisted: draft("धन्यवाद। आप वेटलिस्ट में हैं, LAMP आपके लिए तैयार होते ही हम बताएँगे।"),
  // Settings
  settingsUnreachable: draft("LAMP से जुड़ नहीं पाए। अपना कनेक्शन देखें और फिर से कोशिश करें।"),
  settingsRefused: draft((code: string) => `LAMP ने इसे मना किया (${code})`),
  settingsYes: draft("हाँ"),
  settingsNo: draft("नहीं"),
  settingsEnterNumber: draft("एक संख्या लिखें।"),
  settingsEnterWholeNumber: draft("एक पूरी संख्या लिखें।"),
  settingsLeast: draft((min: number) => `यह कम से कम ${min} हो सकता है।`),
  settingsMost: draft((max: number) => `यह ज़्यादा से ज़्यादा ${max} हो सकता है।`),
  settingsAtLeast: draft((min: number) => `कम से कम ${min}`),
  settingsAtMost: draft((max: number) => `ज़्यादा से ज़्यादा ${max}`),
  settingsDefault: draft("डिफ़ॉल्ट"),
  settingsLockedBy: draft((by: string) => `${by} से लॉक: क़ानून या टेम्पलेट के कारण ज़रूरी`),
  settingsPreviewChange: draft("बदलाव देखें"),
  settingsLoading: draft("सेटिंग्स लोड हो रही हैं…"),
  settingsTreeVersion: draft(
    (version: string) => `बिज़नेस ट्री वर्ज़न ${version}। हर बदलाव लागू होने से पहले दिखाया जाता है।`,
  ),
  settingsWaiting: draft("आपके लिए रुके बदलाव"),
  settingsNothing: draft("अभी यहाँ कुछ सेट नहीं किया जा सकता।"),

  // The Ring the business is on (M3-20b), in Settings, and what changed in LAMP, on the Quality page
  particularsHeading: draft("इनवॉइस पर आपका बिज़नेस"),
  particularsHelp: draft(
    "आपके बिज़नेस का हर टैक्स इनवॉइस इन्हें छापता है। GSTIN के पहले दो अंक पते का राज्य होने चाहिए।",
  ),
  particularsNone: draft("अभी सेट नहीं है: जब तक ये सेट न हों, इनवॉइस जारी नहीं हो सकते।"),
  particularsLegalName: draft("कानूनी नाम"),
  particularsAddress: draft("पता"),
  particularsState: draft("राज्य कोड (दो अंक)"),
  particularsGstin: draft("GSTIN"),
  particularsSignatory: draft("अधिकृत हस्ताक्षरकर्ता"),
  particularsTurnover: draft("पिछले वित्त वर्ष का कुल टर्नओवर (₹)"),
  particularsSave: draft("सेव करें"),
  particularsSaved: draft("सेव हो गया: अब से जारी होने वाले इनवॉइस पर यही छपेगा।"),

  // Settings → Dimension values और Expense accounts (V-10)
  shellDimensionValues: draft("आयाम के मान"),
  shellExpenseAccounts: draft("ख़र्च के खाते"),
  listName: draft("नाम"),
  listAdd: draft("जोड़ें"),
  listRename: draft("नाम बदलें"),
  listRetire: draft("बंद करें"),
  listSave: draft("सेव करें"),
  listCancel: draft("रद्द करें"),
  listNewName: draft((name: string) => `${name} का नया नाम`),
  listRetireSure: draft(
    (name: string) =>
      `“${name}” बंद करें? जो कुछ इसके साथ पोस्ट हुआ उस पर यह बना रहेगा, पर नए काम के लिए इसे चुना नहीं जा सकेगा।`,
  ),
  listRetireConfirm: draft("हाँ, बंद करें"),
  listRetired: draft("बंद"),
  listAdded: draft((name: string, under: string) => `“${name}” ${under} में जुड़ गया।`),
  listRenamed: draft((from: string, to: string) => `“${from}” का नाम अब “${to}” है।`),
  listRetiredNotice: draft((name: string) => `“${name}” बंद हो गया: जो इसके साथ पोस्ट हुआ उस पर यह बना रहेगा।`),
  listReadOnly: draft("आपके रोल आपको इन्हें देखने देते हैं, बदलने नहीं।"),
  listNotAllowed: draft("आपके रोल आपको यह करने नहीं देते: मालिक या खाते रखने वाला कोई व्यक्ति कर सकता है।"),
  dimensionsHelp: draft(
    "हर आयाम के वे मान जिनसे आपका बिज़नेस अपने बिल और एंट्री पहचानता है। बंद किया गया मान पोस्ट हुई चीज़ों पर बना रहता है, पर नए काम के लिए चुना नहीं जा सकता।",
  ),
  dimensionsEmpty: draft((label: string) => `${label} में अभी कुछ नहीं है।`),
  dimensionsCode: draft("कोड (ज़रूरी नहीं)"),
  dimensionsCodeShown: draft((code: string) => `कोड ${code}`),
  dimensionsStandsFor: draft((name: string) => `${name} के लिए`),
  dimensionsStandsForHidden: draft("किसी ग्राहक या सप्लायर के लिए"),
  dimensionsAddTo: draft((label: string) => `${label} में मान जोड़ें`),
  accountsHelp: draft(
    "पैक के खाते यहाँ बदले नहीं जा सकते। उसके समूहों में अपने ख़र्च के खाते जोड़ें: ट्रायल बैलेंस और लाभ-हानि खाता उन्हें वहीं दिखाते हैं।",
  ),
  accountsYours: draft("आपका"),
  accountsPack: draft("पैक से"),
  accountsGroup: draft("समूह"),
  accountsAdd: draft("ख़र्च का खाता जोड़ें"),

  // Settings → Ledger Mapping (V-12)
  shellLedgerMapping: draft("टैली से मिलान"),
  mappingHelp: draft(
    "टैली अपने आप कोई लेजर या कॉस्ट सेंटर नहीं बनाता, इसलिए वाउचर में आने वाले हर खाते, सप्लायर, ग्राहक और आयाम के मान का टैली में नाम चाहिए। LAMP से नाम सुझवाने के लिए अपना टैली मास्टर्स एक्सपोर्ट लगाएँ, या ख़ुद मिलान करें।",
  ),
  mappingAttach: draft("टैली मास्टर्स एक्सपोर्ट फ़ाइल"),
  mappingProposalHeading: draft("LAMP का सुझाव"),
  mappingProposalNote: draft(
    "पुष्टि करने तक कुछ सेव नहीं होता। जो ग़लत है उसका टिक हटाएँ; LAMP जो जोड़ने को कहता है वह तभी जुड़ता है जब आप टिक करें।",
  ),
  mappingNothingNew: draft("इस एक्सपोर्ट में कुछ नया सुझाने को नहीं है।"),
  mappingBy: draft((by: string) => (by === "gstin" ? "GSTIN से" : by === "name" ? "नाम से" : "समूह और नाम से")),
  mappingOfferAccounts: draft("टैली के ये ख़र्च लेजर अपने ख़र्च के खातों में जोड़ें"),
  mappingOfferValues: draft("ये कॉस्ट सेंटर इस आयाम के मानों में जोड़ें:"),
  mappingUnmatchedHeading: draft("मिलान नहीं हुआ: इन्हें ख़ुद मिलाएँ, या छोड़ दें"),
  mappingCandidates: draft("मेल खाते हैं:"),
  mappingConfirm: draft("पुष्टि करें"),
  mappingConfirmed: draft(
    (mapped: number, accounts: number, values: number) =>
      `${mapped} मिलान सेव हुए; ${accounts} ख़र्च के खाते और ${values} मान जुड़े।`,
  ),
  mappingAccounts: draft("खाते"),
  mappingParties: draft("सप्लायर और ग्राहक"),
  mappingValues: draft("आयाम के मान"),
  mappingNothing: draft("अभी यहाँ कुछ नहीं है।"),
  mappingNotMapped: draft("मिलान नहीं हुआ"),
  mappingMap: draft((lamp: string) => `टैली से मिलाएँ: ${lamp}`),
  mappingInTally: draft("टैली में:"),
  mappingMapButton: draft("मिलाएँ"),
  mappingChangeButton: draft("बदलें"),
  mappingSearch: draft((lamp: string) => `${lamp} का टैली नाम`),
  mappingTallyNameField: draft("टैली में नाम"),
  mappingFound: draft("आपके पिछले मास्टर्स एक्सपोर्ट से टैली के नाम"),
  mappingSaved: draft((lamp: string, name: string) => `“${lamp}” अब “${name}” से मिला है।`),
  ringHeading: draft("LAMP की नई रिलीज़"),
  ringAlpha: draft("LAMP की नई रिलीज़ जल्दी पाएँ (अल्फ़ा)"),
  ringAlphaHelp: draft(
    "आपका बिज़नेस हर नई रिलीज़ पर LAMP के अपने बिज़नेस के ठीक बाद, ज़्यादातर दूसरों से पहले जाता है। आप कभी भी बाहर हो सकते हैं।",
  ),
  ringInternal: draft("आपका बिज़नेस LAMP की अंदरूनी रिंग में है: नई रिलीज़ सबसे पहले उस तक पहुँचती है।"),
  ringSaved: draft((alpha: boolean) =>
    alpha
      ? "हो गया: नई रिलीज़ आपके बिज़नेस तक जल्दी पहुँचेगी।"
      : "हो गया: नई रिलीज़ आपके बिज़नेस तक ज़्यादातर दूसरों के साथ पहुँचेगी।",
  ),
  ringName: draft((ring: "internal" | "alpha" | "general") =>
    ring === "internal" ? "LAMP की अंदरूनी रिंग" : ring === "alpha" ? "अल्फ़ा रिंग" : "सामान्य रिंग",
  ),
  qualityChangesHeading: draft("LAMP में क्या बदला"),
  qualityOnRelease: draft((release: string, ring: string) => `आपका बिज़नेस रिलीज़ ${release} पर है, ${ring} में।`),
  qualityNoChanges: draft("आपके बिज़नेस के लिए LAMP अभी नहीं बदला है।"),
  qualityRelease: draft((release: string, at: string) => `रिलीज़ ${release}, ${at}`),
  qualityRolledBack: draft(
    (previous: string) => `वापस लाया गया: रिलीज़ ${previous} में जो बदला था, वह अब लागू नहीं है।`,
  ),
  qualityNothingForYou: draft("आपके बिज़नेस के काम में कुछ नहीं बदला।"),
  qualityOwnersOnly: draft("LAMP में क्या बदला, यह केवल मालिक देखते हैं।"),
  qualityLoading: draft("क्या बदला, लोड हो रहा है…"),
  ladderHeading: draft("LAMP अपने आप कितना करता है"),
  ladderLoading: draft("LAMP अपने आप कितना करता है, लोड हो रहा है…"),
  ladderIntro: draft(
    "हर तरह के काम के लिए: वह किस स्तर पर चलता है, LAMP के परीक्षणों में उसने सबसे ऊँचा कौन-सा स्तर कमाया, और यहाँ उसके काम का क्या हुआ। आप इसे कभी भी घटा सकते हैं; बढ़ा केवल उतना सकते हैं जितना उसने कमाया है।",
  ),
  ladderReadOnly: draft("इस सीढ़ी पर काम को ऊपर या नीचे केवल मालिक करते हैं।"),
  ladderNothing: draft("जो Value Stream आप देख सकते हैं, उनमें LAMP अभी अपने आप कोई काम नहीं करता।"),
  ladderRungs: draft("पहले मैनुअल, फिर पूछकर, फिर अपने आप"),
  ladderName: draft((name: string, taskClass: string) => TASK_CLASSES_HI[taskClass] ?? name),
  autonomyLevel: draft((level: "manual" | "ask" | "auto"): string =>
    level === "manual" ? "मैनुअल" : level === "ask" ? "पूछकर" : "अपने आप",
  ),
  ladderEarned: draft(
    (level: string, on: string) => `LAMP के परीक्षणों में सबसे ऊँचा कमाया स्तर: ${level} (परीक्षण ${on})।`,
  ),
  ladderNotTested: draft("LAMP के परीक्षणों में अभी कमाया नहीं: तब तक मैनुअल।"),
  ladderRunsAt: draft((level: string) => `${level} पर चलता है`),
  ladderCap: draft((cap: "not_earned" | "paused" | "track_record" | "set_by_you"): string =>
    cap === "not_earned"
      ? "LAMP के परीक्षणों में अभी कमाया नहीं"
      : cap === "paused"
        ? "LAMP ने रोका है"
        : cap === "track_record"
          ? "यहाँ के रिकॉर्ड से घटाया गया"
          : "आपने तय किया",
  ),
  ladderLowered: draft((level: string, reason: string) => `LAMP ने इसे घटाकर ${level} किया: ${reason}।`),
  ladderRaiseBackAlso: draft("इसके काम भी सीढ़ी पर ऊपर करें"),
  ladderRaiseBack: draft((level: string) => `वापस बढ़ाकर ${level} करें`),
  ladderNotNow: draft("अभी नहीं"),
  ladderRaisedBack: draft((level: string) => `वापस बढ़ाया: अब यह ${level} तक चल सकता है।`),
  ladderNotSuggested: draft("हटाया: LAMP 30 दिन तक यह सुझाव फिर नहीं देगा।"),
  ladderCounts: draft(
    (days: number, drafted: number, unchanged: number, changed: number, rejected: number, later: number) =>
      `पिछले ${days} दिन: ${drafted} तैयार किए, आपने ${unchanged} बिना बदले मंज़ूर किए, ${changed} बदले, ${rejected} लौटाए; ${later} बाद में सुधारे गए।`,
  ),
  ladderLower: draft((level: string) => `घटाकर ${level} करें`),
  ladderRaise: draft((level: string) => `बढ़ाकर ${level} करें`),

  // A Tree Change's preview
  changeApplied: draft("बदल गया: अब से आपका कारोबार इस बदलाव के अनुसार चलेगा।"),
  changeMoved: draft(
    (moved: number, stayed: number) => `${openRuns(moved)} इस पर लाए गए; ${stayed} अपने पुराने वर्ज़न पर रहे।`,
  ),
  changePreviewLabel: draft("बदलाव की झलक"),
  changeKeepVersion: draft("खुले रन अपने शुरू वाले वर्ज़न पर ही रहते हैं, जब तक आप उन्हें न बदलें।"),
  changeAlsoMove: draft((n: number) => `जिन ${openRuns(n)} का स्टेप अब भी है, उन्हें भी नए वर्ज़न पर ले जाएँ`),
  changeConfirm: draft("बदलाव पक्का करें"),
  changeNotNow: draft("अभी नहीं"),
  changeAppliedStatus: draft("लागू हुआ: अब आपका कारोबार ऐसे ही चलता है।"),
  changeSentToOwner: draft("मालिक को सुझाव के रूप में भेजा गया। मालिक के पक्का करने पर लागू होगा।"),
  changeReplayLabel: draft("आपके हाल के रन पर जाँचा गया"),
  changeBlocked: draft("यह बदलाव पक्का नहीं हो सकता:"),
  changeAgentTestLabel: draft("एजेंट से जाँचें"),
  changeAgentTestOffer: draft(
    (runs: number) =>
      `हाल के ${runs} रन नहीं जाँचे गए, क्योंकि एजेंट के निर्देश बदले हैं। एजेंट उस समय पढ़ी गई जानकारी पर नए निर्देशों से फिर जवाब दे सकता है।`,
  ),
  changeAgentTest: draft((credits: string) => `एजेंट से जाँचें (≈ ${credits} क्रेडिट)`),
  changeAgentSkip: draft("छोड़ें"),
  changeAgentSkipped: draft("एजेंट से नहीं जाँचा गया: वे रन बिना जाँच के रहेंगे। आप बदलाव फिर भी पक्का कर सकते हैं।"),
  changeAgentTestedLabel: draft("एजेंट के नए जवाब"),

  // Settings → Privacy
  privacyUnreachable: draft("LAMP से जुड़ नहीं पाए। अपना कनेक्शन देखें और फिर से कोशिश करें।"),
  privacyLoading: draft("गोपनीयता लोड हो रही है…"),
  privacyStatusAwaitingCode: draft("आपके कोड का इंतज़ार"),
  privacyStatusVerified: draft("पक्का हुआ"),
  privacyStatusForwarded: draft("फ़ैसला करने वाले के पास"),
  privacyStatusCompleted: draft("पूरा हुआ"),
  privacyStatusRefused: draft("मना किया, कारण के साथ"),
  privacyPassedToBusiness: draft(
    (reference: string, date: string) =>
      `अनुरोध ${reference} ${date} को बिज़नेस को भेजा गया। बिज़नेस अपने डेटा के बारे में फ़ैसला करके आपको जवाब देगा।`,
  ),
  privacyConfirmedForBusiness: draft(
    (reference: string, date: string) => `अनुरोध ${reference} पक्का हुआ। LAMP इसे ${date} तक बिज़नेस को भेजेगा।`,
  ),
  privacyWithGrievanceOfficer: draft(
    (reference: string, date: string) =>
      `अनुरोध ${reference} LAMP के शिकायत अधिकारी के पास है, जो इसे ${date} तक पूरा करेंगे।`,
  ),
  privacyConfirmedForLamp: draft(
    (reference: string, date: string) =>
      `अनुरोध ${reference} पक्का हुआ। यह जल्द LAMP के शिकायत अधिकारी तक पहुँचेगा, जो इसे ${date} तक पूरा करेंगे।`,
  ),
  privacyPurposeAccount: draft("आपका LAMP खाता चलाना: साइन-इन, आपके बिज़नेस और सर्विस संदेश"),
  privacyPurposeSensitiveData: draft("आपका PIN और, खाताधारक के लिए, भुगतान की जानकारी (संवेदनशील निजी डेटा)"),
  privacyPurposeQualityMonitoring: draft("आपके रन से बने छिपाए गए क्वालिटी चेक और टेस्ट केस"),
  privacyPurposeProductNews: draft("प्रोडक्ट की ख़बरें (वैकल्पिक)"),
  privacyRightAccess: draft("LAMP के पास मेरा जो डेटा है, वह देखना"),
  privacyRightCorrection: draft("मेरा डेटा ठीक या अपडेट करना"),
  privacyRightErasure: draft("मेरा डेटा मिटाना"),
  privacyRightWithdrawConsent: draft("मेरी सहमति वापस लेना"),
  privacyRightNominate: draft("मेरी ओर से किसी और को चुनना"),
  privacyRightGrievance: draft("शिकायत करना"),
  privacyNoticeVersion: draft((version: string) => `गोपनीयता सूचना वर्ज़न ${version}`),
  privacyWhatFor: draft("किसलिए"),
  privacyYourAnswer: draft("आपका जवाब"),
  privacyGiven: draft("दी गई"),
  privacyWithdrawn: draft("वापस ली गई"),
  privacyNotAsked: draft("अभी पूछा नहीं गया"),
  privacyConsentDetail: draft(
    (date: string, notice: string, channel: string) => `${date}, सूचना ${notice}, ${channel}`,
  ),
  privacyNoticeChanged: draft("सूचना बदल गई है: कृपया फिर से जवाब दें"),
  privacyWhatItStops: draft("इससे क्या रुकेगा"),
  privacyStopsSensitiveData: draft(
    "LAMP आपका PIN तुरंत मिटा देता है, इसलिए साइन इन करने और किसी संवेदनशील काम की पुष्टि के लिए नया कोड लगेगा। अगर आप खाताधारक हैं और मासिक प्लान का भुगतान करते हैं, तो LAMP उसका सेव किया गया UPI ऑटोपे या कार्ड मैंडेट भी रद्द कर देता है, ताकि आगे कुछ न कटे: जिस अवधि का भुगतान हो चुका है वह अपने अंत तक चलती है, और उसके बाद LAMP जारी रखने के लिए आप Plan and billing में प्लान फिर से चुनते हैं, जो आपकी भुगतान जानकारी फिर से माँगता है।",
  ),
  privacyStopsQualityMonitoring: draft(
    "अब से, जिन रन में आप शामिल होंगे, वे LAMP के क्वालिटी चेक और असली रन से बने टेस्ट केस में शामिल नहीं होंगे। जो पहले जाँचा जा चुका है, वह वापस नहीं लिया जाता।",
  ),
  privacyStopsProductNews: draft(
    "LAMP आपको किसी भी चैनल में प्रोडक्ट की ख़बरें नहीं भेजेगा। सर्विस संदेश, जैसे साइन-इन कोड, टास्क, मंज़ूरियाँ और इनवॉइस, फिर भी आते रहेंगे।",
  ),
  privacyStopsAccount: draft(
    "इस सहमति के बिना LAMP आपका खाता खुला नहीं रख सकता, क्योंकि इसके बिना वह आपको साइन इन नहीं करा सकता। इसके बजाय, नीचे अपना खाता मिटाने को कहें (आपके LAMP खाते के बारे में मिटाने का अनुरोध)।",
  ),
  privacyStopsAccountHolder: draft(
    'अगर आप खाताधारक हैं, तो इसके बजाय नीचे "LAMP छोड़ें" में पूरे बिज़नेस को ऑफ़बोर्ड कर सकते हैं।',
  ),
  privacyConfirmWithdraw: draft("सहमति वापस लें"),
  privacyKeepConsent: draft("रहने दें"),
  privacyGiveConsent: draft("सहमति दें"),
  privacyWithdraw: draft("वापस लें"),
  privacyTooManyRequests: draft(
    (time: string) => `बहुत ज़्यादा अनुरोध किए गए हैं, इसलिए कोई कोड नहीं भेजा गया। ${time} के बाद फिर से कोशिश करें।`,
  ),
  privacyCodeFailed: draft("यह कोड नहीं चला। इसे जाँचें, या नए कोड के लिए फिर से अनुरोध करें।"),
  privacyConfirmRequest: draft("अपना अनुरोध पक्का करें"),
  privacyRequestReceived: draft(
    (reference: string, date: string, contact: string) =>
      `LAMP को आपका अनुरोध ${reference} मिला है और यह ${date} तक पूरा होगा। अगर ${contact} LAMP में दर्ज है, तो वहाँ एक कोड आ रहा है: यह आप ही हैं, यह पक्का करने के लिए उसे लिखें।`,
  ),
  privacyCode: draft("कोड"),
  privacyConfirm: draft("पक्का करें"),
  privacyMakeRequest: draft("अनुरोध करें"),
  privacyWhatYouWant: draft("आप क्या चाहते हैं"),
  privacyWhatAbout: draft("यह किस बारे में है"),
  privacyAboutAccount: draft("मेरा LAMP खाता: मेरा नाम, साइन-इन की जानकारी और मेरे बिज़नेस। फ़ैसला LAMP करता है।"),
  privacyAboutBusiness: draft(
    "किसी बिज़नेस का LAMP में रखा डेटा, जैसे उसके ग्राहक या सप्लायर के रिकॉर्ड। फ़ैसला वह बिज़नेस करता है; LAMP आपका अनुरोध 72 घंटे में उसे भेजता है।",
  ),
  privacyYourRegistered: draft("आपका दर्ज"),
  privacyEmail: draft("ईमेल"),
  privacyMobile: draft("मोबाइल नंबर"),
  privacyRegisteredEmail: draft("दर्ज ईमेल"),
  privacyRegisteredMobile: draft("दर्ज मोबाइल नंबर"),
  privacyTellMore: draft("और बताएँ"),
  privacySendRequest: draft("अनुरोध भेजें"),
  privacyNoRequests: draft("आपने कोई अनुरोध नहीं किया है।"),
  privacyDoneOn: draft((date: string) => `${date} को`),
  privacyDue: draft((date: string) => `${date} तक`),
  privacyBusinessRequestsHeading: draft("आपके बिज़नेस के डेटा के बारे में अनुरोध"),
  privacyBusinessRequestsIntro: draft(
    "लोग आपके बिज़नेस के रखे डेटा के बारे में LAMP से पूछते हैं; LAMP हर अनुरोध आपको भेजता है। फ़ैसला और जवाब आपका बिज़नेस देता है। GST रिकॉर्ड रखने ज़रूरी हैं, इसलिए जब कोई अनुरोध अभी पूरा न हो सके तो यह बताएँ।",
  ),
  privacyNoneWaiting: draft("कोई अनुरोध बाकी नहीं है।"),
  privacyOverdue: draft((n: number) =>
    n === 1 ? "1 अनुरोध वादे के 15 दिन से ज़्यादा पुराना है।" : `${n} अनुरोध वादे के 15 दिन से ज़्यादा पुराने हैं।`,
  ),
  privacyWhatYouDid: draft("आपने क्या किया"),
  privacyMarkDone: draft("पूरा हुआ मानें"),
  privacyRefuse: draft("इस कारण से मना करें"),
  privacyReviewerHeading: draft("LAMP में किसने आपका डेटा देखा"),
  privacyReviewerIntro: draft(
    "LAMP अपने एजेंट का काम छिपाए गए डेटा पर जाँचता है। रिव्यूअर बिना छिपा डेटा सिर्फ़ किसी ख़ास दिक़्क़त को ठीक करने के लिए देखता है, और कारण बताता है। हर बार यहाँ लिखा है।",
  ),
  privacyNobodyLooked: draft("LAMP में किसी ने आपके बिज़नेस का डेटा नहीं देखा।"),
  privacyWhen: draft("कब"),
  privacyWho: draft("किसने"),
  privacyWhat: draft("क्या"),
  privacyMasked: draft("छिपा"),
  privacyWhy: draft("क्यों"),
  privacyUnmasked: draft("बिना छिपा"),
  privacyTestCase: draft(
    (id: string, shared: boolean) => `टेस्ट केस ${id} (${shared ? "साझा, छिपा" : "आपके बिज़नेस के लिए रखा"})`,
  ),
  privacyExportHeading: draft("आपके बिज़नेस का डेटा"),
  privacyExportIntro: draft(
    "LAMP में बिज़नेस का सब कुछ डाउनलोड करें: डॉक्यूमेंट, लेजर, रिकॉर्ड और डेटा डिक्शनरी, CSV और JSON फ़ाइलों में। LAMP पहले पक्का करता है कि यह आप हैं।",
  ),
  privacyExported: draft((file: string) => `${file} डाउनलोड हो गई।`),
  privacyStartOffboardingWhat: draft(
    "आप बिज़नेस को LAMP से हटाना शुरू करने जा रहे हैं। इसके बाद डेटा डाउनलोड करने के लिए 30 दिन होंगे।",
  ),
  privacyShredWhat: draft(
    "आप बिज़नेस की बैंक जानकारी और ID नंबर क्रिप्टो-श्रेड करने जा रहे हैं। इसे वापस नहीं किया जा सकता।",
  ),
  privacyLeaveHeading: draft("LAMP छोड़ें"),
  privacyOnlyHolder: draft("बिज़नेस को सिर्फ़ खाताधारक ही हटा सकता है।"),
  privacyShredded: draft("यह बिज़नेस हटा दिया गया। इसकी बैंक जानकारी और ID नंबर क्रिप्टो-श्रेड कर दिए गए।"),
  privacyOffboardIntro: draft(
    "बिज़नेस LAMP छोड़ता है तो उसके पास सब कुछ डाउनलोड करने के लिए 30 दिन होते हैं: डॉक्यूमेंट, लेजर, रिकॉर्ड और डेटा डिक्शनरी, CSV और JSON फ़ाइलों में जिन्हें कोई भी स्प्रेडशीट या अकाउंटिंग सिस्टम पढ़ सके।",
  ),
  privacyStartOffboarding: draft("LAMP छोड़ना शुरू करें"),
  privacyDownloadBy: draft(
    (date: string) =>
      `एक्सपोर्ट ${date} तक डाउनलोड करें, ऊपर "आपके बिज़नेस का डेटा" में "बिज़नेस का सारा डेटा डाउनलोड करें" से।`,
  ),
  privacyEndOf30Days: draft("30 दिन पूरे होने"),
  privacyDownloadAll: draft("बिज़नेस का सारा डेटा डाउनलोड करें"),
  privacyConfirmEach: draft(
    "LAMP के बिज़नेस को क्रिप्टो-श्रेड करने से पहले, इनमें से हर एक पक्का करें। LAMP हर एक का रिकॉर्ड रखता है।",
  ),
  privacyArchive: draft("एक आर्काइव (वैकल्पिक)"),
  privacyShredNow: draft("बिज़नेस को अभी क्रिप्टो-श्रेड करें"),
  privacyStay: draft("LAMP पर बने रहें"),
  privacyConsentRecorded: draft("धन्यवाद। LAMP ने आपकी सहमति आज की तारीख़ और सूचना वर्ज़न के साथ दर्ज की।"),
  privacyConsentWithdrawn: draft(
    "वापस ली गई। जो इस पर निर्भर था, उसे LAMP ने अभी से रोक दिया है, और पहले किया गया काम नहीं पलटता। सुरक्षा लॉग और बिलिंग रिकॉर्ड फिर भी क़ानून के अनुसार रखे जाते हैं।",
  ),
  privacyIntro: draft(
    "LAMP आपके निजी डेटा के साथ क्या करता है, यह उसकी गोपनीयता सूचना में है। और कौन इसे प्रोसेस करता है, यह",
  ),
  privacySubProcessorLink: draft("सब-प्रोसेसर सूची"),
  privacyIntroEnd: draft(" में है।"),
  privacyConsentHeading: draft("आपकी सहमति"),
  privacyRightsHeading: draft("आपके अधिकार"),
  privacyRightsIntro: draft(
    "अपना डेटा देखें, ठीक करें या मिटाएँ, सहमति वापस लें, अपनी ओर से किसी को चुनें, या शिकायत करें। LAMP तुरंत पावती देता है और 15 दिन में पूरा करता है। कोई फ़ीस नहीं है।",
  ),
  privacyRequestsHeading: draft("आपके अनुरोध"),

  // The public signup page
  signupTitle: draft("LAMP का 30 दिन का ट्रायल शुरू करें"),
  signupIntro: draft("कार्ड की ज़रूरत नहीं। आप ही हैं, यह पक्का करने के लिए हम आपको एक कोड ईमेल करेंगे।"),
  signupBusiness: draft("बिज़नेस का नाम"),
  signupName: draft("आपका नाम"),
  signupEmail: draft("ईमेल"),
  signupConsent: draft("मैं सहमत हूँ कि LAMP मेरी जानकारी अपनी प्राइवेसी सूचना के अनुसार इस्तेमाल करे।"),
  signupPrivacyLink: draft("LAMP आपका डेटा कैसे सुरक्षित रखता है"),
  signupSubmit: draft("मुझे कोड ईमेल करें"),
  signupSent: draft((email: string) => `हमने ${email} पर एक कोड भेजा है। यह 10 मिनट तक चलेगा।`),
  signupCode: draft("कोड"),
  signupTrust: draft("इस डिवाइस पर मुझे साइन इन रखें"),
  signupVerify: draft("पक्का करें और शुरू करें"),
  signupResend: draft("नया कोड भेजें"),
  signupResent: draft("नया कोड भेजा जा रहा है।"),
  signupTooMany: draft("अभी यहाँ से बहुत सारे साइनअप हुए हैं। एक घंटे बाद फिर कोशिश करें।"),
  signupCheck: draft("अपनी जानकारी जाँचें, फिर कोशिश करें।"),
  signupBadCode: draft("यह कोड नहीं चला। इसे जाँचें, या नया कोड माँगें।"),
  signupFailed: draft("हमारी तरफ़ कुछ गड़बड़ हुई। कुछ मिनट बाद फिर कोशिश करें।"),
  signupNotServed: draft("ऑर्डर पर माल बनाने वाले निर्माता नहीं हैं?"),
  signupWaitlistLink: draft("वेटलिस्ट में जुड़ें"),
  signupHaveAccount: draft("पहले से LAMP इस्तेमाल करते हैं?"),
  signupSignInLink: draft("साइन इन करें"),

  // The public waitlist page
  waitlistTitle: draft("LAMP की वेटलिस्ट में जुड़ें"),
  waitlistIntro: draft(
    "LAMP आज ऑर्डर पर माल बनाने वाले निर्माताओं के लिए है। अपने बिज़नेस के बारे में बताइए, और जब LAMP आपके काम आ सकेगा, हम आपको बताएँगे।",
  ),
  waitlistName: draft("आपका नाम"),
  waitlistBusiness: draft("बिज़नेस का नाम"),
  waitlistContact: draft("मोबाइल नंबर या ईमेल"),
  waitlistCity: draft("शहर"),
  waitlistIndustry: draft("आपका बिज़नेस क्या करता है"),
  waitlistIndustryPick: draft("एक चुनें"),
  waitlistIndustries: draft([
    { id: "machining", label: "ग्राहक की ड्रॉइंग पर पार्ट्स की मशीनिंग" },
    { id: "fabrication", label: "ऑर्डर पर शीट-मेटल या स्ट्रक्चरल फ़ैब्रिकेशन" },
    { id: "assembly", label: "ऑर्डर पर मशीन या पैनल की असेंबली" },
    { id: "moulding", label: "ऑर्डर पर मोल्डिंग" },
    { id: "casting", label: "ऑर्डर पर कास्टिंग या फ़ोर्जिंग" },
    { id: "trading", label: "ट्रेडिंग या डिस्ट्रीब्यूशन" },
    { id: "retail", label: "दुकान" },
    { id: "food", label: "रेस्टोरेंट, बेकरी या खाना" },
    { id: "services", label: "सेवाएँ" },
    { id: "other", label: "कुछ और" },
  ] as readonly { readonly id: string; readonly label: string }[]),
  waitlistWants: draft("आप LAMP से क्या करवाना चाहते हैं?"),
  waitlistPricesHeading: draft("आप LAMP के लिए महीने के कितने ₹ देंगे?"),
  waitlistTooCheap: draft("इतना सस्ता कि आपको शक हो कि यह काम करता है"),
  waitlistCheap: draft("सस्ता सौदा"),
  waitlistExpensive: draft("महँगा लगने लगे, पर फिर भी सोचने लायक"),
  waitlistTooExpensive: draft("इतना महँगा कि सोचें भी नहीं"),
  waitlistConsent: draft(
    "मैं सहमत हूँ कि LAMP ये जवाब रखे और LAMP के बारे में मुझसे संपर्क करे, जैसा उसकी प्राइवेसी सूचना में लिखा है।",
  ),
  waitlistSubmit: draft("वेटलिस्ट में जुड़ें"),
  waitlistJoined: draft("धन्यवाद। आप वेटलिस्ट में हैं, और जब LAMP आपके काम आ सकेगा, हम आपको बताएँगे।"),
  waitlistCheck: draft("अपने जवाब जाँचें, फिर कोशिश करें।"),
  waitlistPricesOrder: draft("चारों दाम क्रम से बढ़ते हैं: बहुत सस्ता, सस्ता सौदा, महँगा, बहुत महँगा।"),
  waitlistTooMany: draft("अभी यहाँ से बहुत सारी एंट्री हुई हैं। बाद में फिर कोशिश करें।"),

  // The public sub-processor list
  subProcessorsTitle: draft("LAMP के लिए निजी डेटा कौन प्रोसेस करता है"),
  subProcessorsIntro: draft(
    'ये बाहरी कंपनियाँ LAMP की होस्टेड सेवा के लिए निजी डेटा प्रोसेस करती हैं। कोई नई कंपनी जोड़ने, या किसी को मिलने वाला डेटा या जगह बदलने से कम से कम 30 दिन पहले LAMP खाताधारकों को बताता है। "पक्का हो रहा है" वाली कंपनियाँ तय हैं, पर उनका कॉन्ट्रैक्ट, क्षेत्र या शर्तें अभी जाँची जा रही हैं।',
  ),
  subProcessorsProvider: draft("कंपनी"),
  subProcessorsWhatTheyDo: draft("वे क्या करते हैं"),
  subProcessorsWhatTheyReceive: draft("उन्हें क्या मिलता है"),
  subProcessorsWhere: draft("कहाँ"),
  subProcessorsBusinessData: draft("बिज़नेस का डेटा"),
  subProcessorsBeingConfirmed: draft("पक्का हो रहा है"),
  subProcessorsTenantDataYes: draft("हाँ"),
  subProcessorsTenantDataWhenEnabled: draft("चालू होने पर"),
  subProcessorsTenantDataLampOnly: draft("सिर्फ़ LAMP का"),
  subProcessorsNotListed: draft(
    "इस सूची में नहीं: वे असिस्टेंट जिन्हें आप ख़ुद LAMP से जोड़ते हैं, बिज़नेस की फ़ाइलिंग लेने वाले सरकारी पोर्टल, और वह अकाउंटिंग सॉफ़्टवेयर जिसमें बिज़नेस एक्सपोर्ट करता है। इन्हें डेटा तभी मिलता है जब आप या बिज़नेस चुनें।",
  ),
  subProcessorsBack: draft("LAMP पर वापस"),
  subProcessorsGroupHosting: draft("होस्टिंग और इंफ्रास्ट्रक्चर"),
  subProcessorsGroupModels: draft("AI मॉडल"),
  subProcessorsGroupMessaging: draft("मैसेजिंग चैनल"),
  subProcessorsGroupEmail: draft("ईमेल और SMS"),
  subProcessorsGroupPayments: draft("भुगतान"),
  subProcessorsAwsPurpose: draft("LAMP को होस्ट करता है: सर्वर, डेटाबेस, फ़ाइल स्टोरेज, बैकअप और लॉग"),
  subProcessorsAwsReceives: draft("LAMP में रखा सारा डेटा, एन्क्रिप्ट करके"),
  subProcessorsAwsCountry: draft("भारत (मुंबई)"),
  subProcessorsAnthropicPurpose: draft("मुश्किल कामों में LAMP के एजेंट के लिए डॉक्यूमेंट पढ़ता और ड्राफ़्ट बनाता है"),
  subProcessorsAnthropicReceives: draft("सिर्फ़ काम के लिए ज़रूरी टेक्स्ट, जहाँ हो सके वहाँ नाम और नंबर छिपाकर"),
  subProcessorsOpenRouterProvider: draft("OpenRouter, Inc., और वे मॉडल होस्ट जिन तक यह भेजता है"),
  subProcessorsOpenRouterPurpose: draft("रोज़ के काम छोटे मॉडल तक भेजता है"),
  subProcessorsAlternativePurpose: draft("एक दूसरा मॉडल, अगर काम उस पर ठीक चलता है"),
  subProcessorsMaskedReceives: draft("सिर्फ़ काम के लिए ज़रूरी टेक्स्ट, जहाँ हो सके वहाँ छिपाकर"),
  subProcessorsUnitedStates: draft("अमेरिका"),
  subProcessorsUsEu: draft("अमेरिका, यूरोपीय संघ"),
  subProcessorsTelegramPurpose: draft("Telegram चैनल और साइन-इन कोड"),
  subProcessorsTelegramReceives: draft("LAMP के बॉट से संदेश, Telegram ID, नाम और पक्का मोबाइल नंबर"),
  subProcessorsOutsideIndia: draft("भारत के बाहर"),
  subProcessorsWhatsAppPurpose: draft("WhatsApp चैनल, चालू होने पर"),
  subProcessorsWhatsAppReceives: draft(
    "संदेश, फ़ाइलें, WhatsApp नंबर और प्रोफ़ाइल नाम। साझा सीखने के लिए कभी इस्तेमाल नहीं",
  ),
  subProcessorsWhatsAppCountry: draft("अमेरिका और Meta के दूसरे डेटा सेंटर"),
  subProcessorsSesPurpose: draft("ईमेल से साइन-इन कोड, न्योते और सूचनाएँ; बिज़नेस के ईमेल किए डॉक्यूमेंट"),
  subProcessorsSesReceives: draft("ईमेल पता, संदेश और अटैचमेंट"),
  subProcessorsIndia: draft("भारत"),
  subProcessorsSmsProvider: draft("एक SMS कंपनी, अगर चालू हो"),
  subProcessorsSmsPurpose: draft("SMS से साइन-इन कोड"),
  subProcessorsSmsReceives: draft("मोबाइल नंबर और कोड"),
  subProcessorsRazorpayPurpose: draft("LAMP की सब्सक्रिप्शन फ़ीस लेता है"),
  subProcessorsRazorpayReceives: draft("खाताधारक की बिलिंग जानकारी; Razorpay के अपने पेज पर डाली गई भुगतान जानकारी"),

  // The public trust page
  trustTitle: draft("LAMP कैसे काम करता है और आपके डेटा को सुरक्षित कैसे रखता है"),
  trustOnThisPage: draft("इस पेज पर"),
  // The legal notices (M3-29c)
  legalPrivacyNotice: draft("गोपनीयता सूचना"),
  legalContactAndGrievances: draft("संपर्क और शिकायतें"),
  legalVersion: draft((version: string) => `संस्करण ${version}`),
  legalReadInEnglish: draft("अंग्रेज़ी संस्करण पढ़ें"),
  legalDraftBody: draft("किसी वकील ने अभी इस सूचना की समीक्षा नहीं की है। यह केवल डेवलपमेंट सेल पर दिखाई जाती है।"),
  legalUnavailableTitle: draft("यह सूचना अभी प्रकाशित नहीं हुई है"),
  legalUnavailable: draft("वकील की समीक्षा के बाद ही LAMP अपनी कानूनी सूचनाएँ प्रकाशित करता है।"),
  trustDraft: draft("ड्राफ़्ट।"),
  trustDraftBody: draft(
    "इस पेज के कुछ हिस्सों की अभी तक किसी वकील ने समीक्षा नहीं की है। यह केवल डेवलपमेंट Cell पर दिखाया जाता है।",
  ),
  trustSectionDraft: draft("ड्राफ़्ट"),
  trustUnpublishedTitle: draft("LAMP ट्रस्ट पेज"),
  trustUnpublished: draft("यह पेज अभी प्रकाशित नहीं हुआ है।"),
  trustBack: draft("LAMP पर वापस जाएँ"),
  trustRecoveryHeading: draft("रिकवरी, जैसी हमारी पिछली रिस्टोर ड्रिल में हासिल हुई"),
  trustRecoveryDrill: draft(
    (date: string, tested: string) => `${date} की रिस्टोर ड्रिल में, जिसमें ${tested} जाँचा गया:`,
  ),
  trustRecoveryTime: draft("LAMP इतने समय में वापस चालू हुआ (रिकवरी टाइम)"),
  trustRecoveryPoint: draft("अधिकतम इतने समय का डेटा खोया (रिकवरी पॉइंट)"),
  trustDuration: draft((hours: number, minutes: number) =>
    hours
      ? minutes
        ? `${hours} ${unitHi("hours", hours === 1)} ${minutes} मिनट`
        : `${hours} ${unitHi("hours", hours === 1)}`
      : `${minutes} मिनट`,
  ),
  trustNoReport: draft("अभी तक कोई रिपोर्ट प्रकाशित नहीं हुई है।"),
  trustReportPeriod: draft((from: string, to: string) => `${from} से ${to} तक`),
  trustReportFor: draft((period: string) => `${period} की रिपोर्ट`),
  trustReportGenerated: draft(
    (date: string, publishedBy: string) => `दोनों दिन शामिल (UTC)। ${date} को बनाई गई। प्रकाशक: ${publishedBy}।`,
  ),
  trustReportBusinesses: draft((tenants: string) => `इस अवधि में LAMP पर बिज़नेस: ${tenants}।`),
  trustReportSource: draft((source: string) => `स्रोत: ${source}`),
  trustReportFigure: draft("आँकड़ा"),
  trustReportForPeriod: draft("इस अवधि के लिए"),
  trustReportNoChanges: draft("इस अवधि में कोई बदलाव नहीं।"),
  trustRecentIncidents: draft("हाल की घटनाएँ"),
  trustPostmortem: draft("क्या हुआ और हमने क्या बदला"),

  // The Cockpit's Audit and Credits views
  viewAuditLog: draft("ऑडिट लॉग"),
  viewFilterRun: draft("रन"),
  viewFilterObject: draft("बिज़नेस ऑब्जेक्ट"),
  viewFilterObjectId: draft("बिज़नेस ऑब्जेक्ट id"),
  viewFilterActor: draft("व्यक्ति (या सिस्टम)"),
  viewFilterAgent: draft("एजेंट काम की क़िस्म"),
  viewFilterFrom: draft("से"),
  viewFilterTo: draft("तक"),
  viewFilterAfter: draft("इस एंट्री के बाद"),
  viewFilter: draft("छाँटें"),
  viewNoEntries: draft("कोई एंट्री नहीं मिली।"),
  viewWhen: draft("कब"),
  viewAction: draft("काम"),
  viewRun: draft("रन"),
  viewNode: draft("नोड"),
  viewBusinessObject: draft("बिज़नेस ऑब्जेक्ट"),
  viewWho: draft("किसने"),
  viewAgent: draft("एजेंट"),
  viewSystemActor: draft("सिस्टम"),
  viewCredits: draft("क्रेडिट"),
  viewCreditsAmount: draft((credits: string) => `${credits} क्रेडिट`),
  viewCreditsUsed: draft(
    (run: string, credits: string, steps: number, modelCalls: number) =>
      `रन ${run} ने ${credits} इस्तेमाल किए: ${steps} स्टेप, ${modelCalls} मॉडल कॉल।`,
  ),
  viewStepOrTask: draft("स्टेप या काम"),
  viewRunInAudit: draft("ऑडिट लॉग में यह रन"),
  viewSignIn: draft("साइन इन करें"),
  viewSignInBody: draft("कॉकपिट देखने के लिए वेब चैट में साइन इन करें।"),
  viewNotAllowed: draft("अनुमति नहीं"),
  viewNotAllowedBody: draft("आपके रोल से आप इसे नहीं देख सकते।"),
  viewUnavailable: draft("उपलब्ध नहीं"),
  viewUnavailableBody: draft("Application API ने जवाब नहीं दिया। फिर से कोशिश करें।"),
  // नकद और खाते (#486)।
  cashHeading: draft("आपका पैसा कहाँ है"),
  cashIntro: draft(
    (asOf: string) => `${asOf} तक। यह पेज सिर्फ़ दिखाता है: भुगतान या बिल दर्ज करने के लिए Chat में LAMP से कहें।`,
  ),
  cashLoading: draft("नकद और खाते लोड हो रहे हैं…"),
  cashLoadFailed: draft("नकद और खाते लोड नहीं हो सके। थोड़ी देर में फिर से कोशिश करें।"),
  cashRefused: draft(
    "आपके रोल आपको बैंक और नकद बैलेंस देखने नहीं देते, इसलिए यह पेज आपको नहीं दिखाया जाता। कोई Owner रोल और अनुमतियाँ में आपके रोल को बैंक और नकद बैलेंस दे सकता है।",
  ),
  cashEmptyTitle: draft("अभी कुछ दर्ज नहीं है"),
  cashEmptyIntro: draft(
    "जैसे-जैसे आपका कारोबार LAMP में अपना पैसा दर्ज करेगा, यह पेज भरेगा। शुरू करने के लिए Chat में इनमें से कुछ भी दर्ज करें:",
  ),
  cashEmptyInvoice: draft("ग्राहक को इनवॉइस, ताकि दिखे कि ग्राहकों पर आपका कितना बाकी है"),
  cashEmptyBill: draft("सप्लायर का बिल, ताकि दिखे कि आप सप्लायरों को कितना देंगे"),
  cashEmptyPayment: draft("मिला या दिया गया भुगतान, ताकि बैंक और हाथ में नकद दिखे"),
  cashBank: draft("बैंक में पैसा"),
  cashBankUnknown: draft("पता नहीं"),
  cashBankNotGiven: draft((moved: string, since: string | null) =>
    since === null
      ? "ओपनिंग बैलेंस नहीं दिया गया।"
      : `${since} से LAMP में ${moved} का लेन-देन, ओपनिंग बैलेंस नहीं दिया गया।`,
  ),
  cashBankFrom: draft(
    (opening: string, asOf: string | null, fromTally: boolean) =>
      `${asOf === null ? "" : `${asOf} के अंत के `}बैलेंस ${opening} से${fromTally ? ", आपके Tally मास्टर्स में" : ", जैसा दिया गया"}, और उसके बाद LAMP में दर्ज हुआ।`,
  ),
  cashGiveOpening: draft("बैंक का ओपनिंग बैलेंस दें"),
  cashGiveOpeningIntro: draft(
    "किसी दिन के अंत में आपकी बहियों में बैंक का बैलेंस: LAMP उसके बाद के दिनों में दर्ज हुआ जोड़ता है। ओवरड्राफ़्ट माइनस चिह्न के साथ लिखें।",
  ),
  cashGiveOpeningAmount: draft("ओपनिंग बैलेंस (₹)"),
  cashGiveOpeningDate: draft("इस दिन के अंत में"),
  cashForecastNotGiven: draft((since: string | null) =>
    since === null
      ? "पूर्वानुमान बैंक में पैसे से शुरू होता है: उसे देखने के लिए बैंक का ओपनिंग बैलेंस दें।"
      : `पूर्वानुमान बैंक में पैसे से शुरू होता है, जो LAMP ${since} से ही जानता है: उसे देखने के लिए बैंक का ओपनिंग बैलेंस दें।`,
  ),
  cashGiveOpeningSave: draft("सहेजें"),
  cashGiveOpeningInvalid: draft("ओपनिंग बैलेंस रुपये और पैसे में, और उसकी तारीख दें।"),
  cashGiveOpeningFailed: draft((problems: readonly string[]) =>
    problems.length > 0 ? problems.join(" ") : "ओपनिंग बैलेंस सहेजा नहीं जा सका। थोड़ी देर बाद फिर कोशिश करें।",
  ),
  cashInHand: draft("हाथ में नकद"),
  cashOwedToYou: draft("ग्राहकों पर बाकी"),
  cashYouOwe: draft("सप्लायरों को देना"),
  cashReceivables: draft("ग्राहकों पर आपका कितना बाकी है"),
  cashPayables: draft("आप सप्लायरों को कितना देंगे"),
  cashAgeing: draft("कितनी देर हुई"),
  cashBucket: draft(
    (bucket: string) =>
      ({
        not_due: "अभी देय नहीं",
        "1_30": "1–30 दिन देर",
        "31_60": "31–60 दिन देर",
        "61_90": "61–90 दिन देर",
        over_90: "90 दिन से ज़्यादा देर",
      })[bucket] ?? bucket,
  ),
  cashNothingOwedToYou: draft("आज किसी ग्राहक पर आपका कुछ बाकी नहीं है।"),
  cashNothingYouOwe: draft("आज आपको किसी सप्लायर को कुछ नहीं देना है।"),
  cashInvoice: draft("इनवॉइस"),
  cashBill: draft("बिल"),
  cashCustomer: draft("ग्राहक"),
  cashSupplier: draft("सप्लायर"),
  cashDue: draft("देय तारीख"),
  cashDaysLate: draft("कितने दिन देर"),
  cashOwedToYouColumn: draft("आपका बाकी"),
  cashYouOweColumn: draft("आपको देना"),
  cashForecastTitle: draft("नकद का अनुमान, अगले 13 हफ़्ते"),
  cashForecastIntro: draft(
    "खुले इनवॉइस, बिल, पेरोल और वैधानिक देनदारियों से हर हफ़्ते कितना आएगा और जाएगा, आज आपके पास जितना पैसा है उससे शुरू करके।",
  ),
  cashForecastNegative: draft(
    (week: number) => `हफ़्ता ${String(week)} पहला है जिसमें आपका पैसा शून्य से नीचे जाता है।`,
  ),
  cashWeek: draft("हफ़्ता"),
  cashWeekSpan: draft((from: string, to: string) => `${from} – ${to}`),
  cashIn: draft("आया पैसा"),
  cashOut: draft("गया पैसा"),
  cashClosing: draft("बचा पैसा"),
  cashBelowNil: draft("शून्य से नीचे"),
  sensitiveClass: draft(
    (dataClass: string) =>
      ({
        bank_and_cash: "बैंक और नकद बैलेंस",
        payroll: "पेरोल",
        costs_and_margins: "लागत और मार्जिन",
        customer_pricing: "ग्राहकों के दाम",
      })[dataClass] ?? dataClass,
  ),
  cashHidden: draft(
    (classes: readonly string[]) =>
      `आपके रोल यह हिस्सा नहीं दिखाते: इसके लिए ${
        classes.length > 1 ? `${classes.slice(0, -1).join(", ")} और ${classes.at(-1) ?? ""}` : (classes[0] ?? "")
      } भी चाहिए।`,
  ),
  cashCouldNotRead: draft((problems: readonly string[]) => `LAMP यह हिस्सा नहीं पढ़ सका: ${problems.join("; ")}।`),
  cashLedgersTitle: draft("खाते"),
  cashStock: draft("स्टॉक"),
  cashStockHow: draft("आज हाथ में, हर Item उसकी weighted average खरीद लागत पर: closing stock जर्नल यही पोस्ट करेगा।"),
  cashStockUnvalued: draft(
    (items: number) => `${String(items)} Item बिना खरीद या ओपनिंग लागत के रखे हैं, इसलिए उनकी कीमत नहीं लगी।`,
  ),
  cashLedgersIntro: draft(
    "हर वह खाता जिसमें आपके दस्तावेज़ों ने एंट्री की है, उसके बैलेंस और नई एंट्रियों के साथ। किसी एंट्री के दस्तावेज़ से उसका Run, या इनवॉइस की PDF खोलें।",
  ),
  cashAccountName: draft(
    (account: string, name: string) =>
      ({
        bank: "बैंक",
        cash: "हाथ में नकद",
        receivable: "ग्राहक (उन पर बाकी)",
        payable: "सप्लायर (उन्हें देना)",
        sales: "बिक्री",
        purchases: "ख़रीद",
        output_cgst: "आउटपुट CGST",
        output_sgst: "आउटपुट SGST",
        output_igst: "आउटपुट IGST",
        input_cgst: "इनपुट CGST",
        input_sgst: "इनपुट SGST",
        input_igst: "इनपुट IGST",
        round_off: "राउंड-ऑफ़",
        opening_balance_equity: "शुरुआती बैलेंस",
      })[account] ?? name,
  ),
  cashDebitBalance: draft((amount: string) => `${amount} नामे`),
  cashCreditBalance: draft((amount: string) => `${amount} जमा`),
  cashDate: draft("तारीख"),
  cashDocument: draft("दस्तावेज़"),
  cashCounterparty: draft("ग्राहक या सप्लायर"),
  cashDebit: draft("नामे"),
  cashCredit: draft("जमा"),
  cashDocumentName: draft((object: string, number: string | null) => {
    const kind =
      {
        invoice: "टैक्स इनवॉइस",
        payment_receipt: "मिला भुगतान",
        supplier_bill: "सप्लायर का बिल",
        supplier_payment: "सप्लायर को भुगतान",
        credit_note: "क्रेडिट नोट",
        customer_debit_note: "डेबिट नोट",
        goods_receipt: "माल की प्राप्ति",
        stock_receipt: "स्टॉक की प्राप्ति",
        stock_issue: "स्टॉक जारी",
        opening_balance: "शुरुआती बैलेंस",
        payroll_run: "पेरोल",
        wage_payment: "वेतन भुगतान",
        statutory_payment: "वैधानिक भुगतान",
        journal: "जर्नल",
      }[object] ?? object.replaceAll("_", " ");
    return number ? `${kind} ${number}` : kind;
  }),
  cashShowing: draft((shown: number, all: number) => `${String(all)} में से नई ${String(shown)} एंट्रियाँ।`),

  // GST और अनुपालन (#486)।
  gstHeading: draft("GST और क्या देय है"),
  gstIntro: draft("यह पेज सिर्फ़ दिखाता है: रिटर्न तैयार करने या फ़ाइलिंग दर्ज करने के लिए Chat में LAMP से कहें।"),
  gstLoading: draft("GST और अनुपालन लोड हो रहा है…"),
  gstLoadFailed: draft("GST और अनुपालन लोड नहीं हो सका। थोड़ी देर में फिर से कोशिश करें।"),
  gstRefused: draft(
    "आपके रोल आपको बैंक और नकद बैलेंस और लागत देखने नहीं देते, जिनसे GST की स्थिति पढ़ी जाती है, इसलिए यह पेज आपको नहीं दिखाया जाता। कोई Owner रोल और अनुमतियाँ में आपका रोल बदल सकता है।",
  ),
  gstPeriodLoading: draft((month: string) => `${month} लोड हो रहा है…`),
  gstPeriodFailed: draft(
    (month: string) => `${month} पढ़ा नहीं जा सका। थोड़ी देर में फिर से कोशिश करें, या कोई और महीना चुनें।`,
  ),
  gstPeriod: draft("अवधि"),
  gstPositionTitle: draft((period: string) => `${period} का GST`),
  gstOutput: draft("आपकी बिक्री पर GST"),
  gstItc: draft("इनपुट टैक्स क्रेडिट"),
  gstNet: draft("कुल देय GST"),
  gstCarried: draft("आगे ले जाया गया क्रेडिट"),
  gstTax: draft("टैक्स"),
  gstTotal: draft("कुल"),
  gstOutputRow: draft("बिक्री और रिवर्स चार्ज पर"),
  gstItcRow: draft("इनपुट टैक्स क्रेडिट"),
  gstPayRow: draft("नकद में देना"),
  gstNothingRecorded: draft(
    (period: string) =>
      `${period} के लिए कुछ भी कर-योग्य दर्ज नहीं है। LAMP में अपने टैक्स इनवॉइस जारी करें और सप्लायरों के बिल दर्ज करें, उनका GST यहाँ दिखेगा।`,
  ),
  gstNotLaidOut: draft(
    "LAMP ने इस अवधि का GSTR-3B अभी तय नहीं किया है: ये वे आँकड़े हैं जो आज उसके ड्राफ़्ट में होंगे।",
  ),
  gstNoVersionYet: draft(
    "इस अवधि का GSTR-3B अभी तैयार नहीं हुआ: ये वे आँकड़े हैं जो आज उसके ड्राफ़्ट में होंगे। इसे तैयार करने के लिए चैट में LAMP से कहें।",
  ),
  gstKeptVersion: draft(
    (version: number, on: string) => `ये GSTR-3B संस्करण ${String(version)} के आँकड़े हैं, जो ${on} को तैयार हुआ।`,
  ),
  gstKeptChanged: draft((version: number, filed: boolean) =>
    filed
      ? `संस्करण ${String(version)} तैयार होने के बाद इस अवधि की पोस्टिंग या उसका GSTR-2B बदला है, इसलिए आज के आँकड़े फ़ाइल किए गए रिटर्न से अलग हैं। अंतर को अगले रिटर्न में कैसे लें, यह अपने CA से पूछें।`
      : `संस्करण ${String(version)} तैयार होने के बाद इस अवधि की पोस्टिंग या उसका GSTR-2B बदला है, इसलिए आज के आँकड़े इनसे अलग हैं। फ़ाइल करने से पहले चैट में LAMP से GSTR-3B फिर से तैयार करने को कहें।`,
  ),
  gstExportsTitle: draft("आपके CA के लिए रिटर्न"),
  gstExportsIntro: draft(
    "हर फ़ाइलिंग के लिए तैयार GSTR-1 फ़ाइल और GSTR-3B ड्राफ़्ट, हर संस्करण रखा हुआ, आपके CA को देने के लिए।",
  ),
  gstNoExports: draft(
    (period: string) =>
      `${period} के लिए अभी कोई GSTR-1 या GSTR-3B तय नहीं है। LAMP का अनुपालन चक्र उन्हें तय कर दे, तब Chat में LAMP से उन्हें तैयार करने को कहें।`,
  ),
  gstExportKind: draft(
    (kind: string) =>
      ({
        gstr1: "GSTR-1",
        gstr3b: "GSTR-3B",
        tds_salary: "वेतन पर TDS रिटर्न (24Q)",
        tds_non_salary: "दूसरे भुगतानों पर TDS रिटर्न (26Q)",
      })[kind] ?? kind,
  ),
  gstExportTitle: draft((what: string, period: string) => `${period} का ${what}`),
  gstNotPrepared: draft(
    (what: string, period: string) => `अभी तैयार नहीं। Chat में LAMP से कहें: “prepare ${what} for ${period}”।`,
  ),
  gstExportHidden: draft((problems: readonly string[]) => `आपके रोल यहाँ तक नहीं पहुँचते: ${problems.join("; ")}।`),
  gstVersion: draft((version: number, on: string) => `संस्करण ${String(version)}, ${on}:`),
  gstFilePart: draft(
    (part: string) =>
      ({
        file: "फ़ाइल डाउनलोड करें",
        draft: "ड्राफ़्ट डाउनलोड करें",
        reconciliation: "GSTR-2B मिलान डाउनलोड करें",
        deductees: "कटौती वालों की शीट डाउनलोड करें",
        salary: "वेतन शीट डाउनलोड करें",
        annual: "सालाना शीट डाउनलोड करें",
      })[part] ?? part,
  ),
  gstTdsTitle: draft("आपके CA के लिए TDS रिटर्न का डेटा"),
  gstNoTds: draft("इस अवधि के लिए कोई TDS रिटर्न तय नहीं है।"),
  gstCalendarTitle: draft((month: string) => `${month} में देय फ़ाइलिंग`),
  gstCalendarIntro: draft(
    "हर वैधानिक फ़ाइलिंग और भुगतान जो देय है, उसकी तारीख के साथ; तारीख निकल जाने पर वह देर से है।",
  ),
  gstCycleOff: draft(
    "आपके कारोबार के लिए LAMP का अनुपालन चक्र चालू नहीं है, इसलिए LAMP इन्हें तय नहीं करता और न ही याद दिलाता है।",
  ),
  gstNothingDue: draft((month: string) => `${month} में कुछ देय नहीं है।`),
  gstFiling: draft("फ़ाइलिंग"),
  gstFor: draft("किसके लिए"),
  gstDueOn: draft("देय तारीख"),
  gstStatus: draft("स्थिति"),
  gstStatusDue: draft("देय"),
  gstStatusDone: draft((on: string) => `${on} को फ़ाइल किया`),
  gstStatusBeforeLamp: draft("LAMP से पहले फ़ाइल किया"),
  gstStatusLate: draft((days: number | null) => (days ? `${String(days)} दिन देर` : "देर से")),
  gstCouldNotRead: draft((problems: readonly string[]) => `LAMP यह हिस्सा नहीं पढ़ सका: ${problems.join("; ")}।`),
  viewNotFound: draft("नहीं मिला"),
  viewNotFoundBody: draft("ऐसा कोई पेज नहीं है।"),

  modelsLoading: draft("AI मॉडल लोड हो रहे हैं…"),
  modelsIntro: draft(
    "LAMP तीन आकार के AI मॉडल इस्तेमाल करता है, और जहाँ नियम से काम चल जाए वहाँ कोई मॉडल नहीं। हर काम उस सबसे छोटे मॉडल को जाता है जो उसे ठीक से कर सके, ताकि आपके क्रेडिट ज़्यादा चलें।",
  ),
  modelsWork: draft("काम का प्रकार"),
  modelsWhat: draft("यह क्या करता है"),
  modelsUsed: draft("इस महीने इस्तेमाल"),
  modelsWhere: draft("कहाँ चलता है"),
  modelsTierName: draft(
    (tier: string) => ({ T0: "कोई मॉडल नहीं", T1: "छोटा मॉडल", T2: "मझोला मॉडल", T3: "सबसे बड़ा मॉडल" })[tier] ?? tier,
  ),
  modelsTierWork: draft(
    (tier: string) =>
      ({
        T0: "रूट, नियम, खातों में पोस्टिंग, दस्तावेज़ छापना और तय संदेश भेजना।",
        T1: "अंग्रेज़ी, हिंदी या हिंग्लिश में संदेश समझना, ब्योरे निकालना और चैट के जवाब लिखना।",
        T2: "दस्तावेज़ों की फ़ोटो और PDF पढ़ना, कोटेशन और अनुमान का मसौदा बनाना, और प्लेबुक के हिसाब से फ़ैसला करना।",
        T3: "काम के नए तरीके और पेचीदा बदलावों का मसौदा बनाना, और मझोले मॉडल का जवाब जाँच में फ़ेल हो तो दोबारा कोशिश।",
      })[tier] ?? "",
  ),
  modelsCalls: draft((_calls: number, shown: string, credits: string) => `${shown} कॉल, ${credits} क्रेडिट`),
  modelsSteps: draft((_steps: number, shown: string, credits: string) => `${shown} स्टेप, ${credits} क्रेडिट`),
  modelsNoModel: draft("कोई मॉडल नहीं बुलाया जाता"),
  modelsOutsideIndia: draft("भारत के बाहर"),
  modelsInsideCell: draft("आपके LAMP के अंदर, भारत में"),
  modelsBudget: draft("मॉडल बजट"),
  modelsBudgetUsed: draft(
    (used: string, budget: string, percent: number) =>
      `इस महीने ${budget} में से ${used} क्रेडिट इस्तेमाल हुए (${percent}%)।`,
  ),
  modelsBudgetLeft: draft((left: string) => `${left} क्रेडिट बचे हैं। ब्योरा और टॉप-अप "क्रेडिट और इस्तेमाल" में है।`),
  modelsData: draft("आपका डेटा और मॉडल"),
  modelsLeavesIndia: draft(
    "कुछ मॉडल कॉल भारत से बाहर जाती हैं: AI मॉडल भारत के बाहर के प्रदाताओं पर चलते हैं। कॉल में जो जाता है वह सिर्फ़ उन प्रदाताओं को जाता है जो इन शर्तों से बँधे हैं:",
  ),
  modelsStaysInIndia: draft("कोई मॉडल कॉल भारत से बाहर नहीं जाती: हर AI मॉडल आपके LAMP के अंदर चलता है।"),
  modelsNoTraining: draft("वे आपके बिज़नेस के डेटा पर अपने मॉडल कभी नहीं सिखाते।"),
  modelsZeroRetention: draft("जवाब देने के बाद वे उसमें से कुछ नहीं रखते।"),
  modelsSensitive: draft(
    "LAMP मॉडल को सिर्फ़ उतना भेजता है जितना काम के लिए ज़रूरी है, और उससे ज़्यादा कभी नहीं जो वह व्यक्ति देख सकता है जिसके लिए वह काम कर रहा है।",
  ),

  securityLoading: draft("सुरक्षा लोड हो रही है…"),
  securitySignIn: draft("आप कैसे साइन इन करते हैं"),
  securityMethod: draft(
    (method: string) =>
      ({
        email: "आपके ईमेल पर भेजा गया कोड",
        telegram: "टेलीग्राम पर आपको भेजा गया कोड",
        sms: "SMS से भेजा गया कोड",
      })[method] ?? method,
  ),
  securityNoMethod: draft("आपके लिए साइन इन का कोई तरीका नहीं है। किसी मालिक से आपको फिर से बुलाने को कहें।"),
  securityPin: draft("एक PIN, जब कोड न आए"),
  securityNoPin: draft("कोई PIN नहीं है।"),
  securityPinNew: draft("नया PIN, छह अंक"),
  securityPinSet: draft("मेरा PIN बनाएँ"),
  securityPinSaved: draft("आपका PIN बन गया है।"),
  securityPinNotSet: draft("कोई PIN नहीं बना।"),
  securityPinWeak: draft(
    "यह PIN आसानी से अंदाज़ा लगाया जा सकता है। छह ऐसे अंक चुनें जो कोई पैटर्न न हों, और फिर कोशिश करें।",
  ),
  securityPinCancel: draft("अभी नहीं"),
  securitySessions: draft("आप कहाँ साइन इन हैं"),
  securitySessionsIntro: draft("पिछले 30 दिनों में हर बार जब आपने साइन इन किया, सबसे नया पहले।"),
  securitySignedIn: draft("साइन इन किया"),
  securityDevice: draft("डिवाइस"),
  securityThisDevice: draft("यह डिवाइस"),
  securityOtherDevice: draft("दूसरा डिवाइस"),
  securityKept: draft("साइन इन रहता है"),
  securityTrusted: draft("30 दिन"),
  securityUntrusted: draft("12 घंटे"),
  securityState: draft("अभी"),
  securityLive: draft("साइन इन है"),
  securityEnded: draft((when: string) => `${when} को साइन आउट हुआ`),
  securityExpired: draft("खत्म हुआ"),
  securitySignOutEverywhere: draft("बाकी हर जगह से साइन आउट करें"),
  securitySignedOutEverywhere: draft((ended: number) =>
    ended === 0 ? "आप और कहीं साइन इन नहीं थे।" : `${ended} दूसरे सेशन से साइन आउट किया।`,
  ),
  securityAssistants: draft("जिन सहायकों के पास पहुँच है"),
  securityBreakGlass: draft("आपके बिज़नेस में LAMP सपोर्ट की पहुँच"),
  securityBreakGlassIntro: draft(
    "LAMP के लोग आपके बिज़नेस का डेटा सिर्फ़ ब्रेक-ग्लास पहुँच से देखते हैं: किसी मालिक की सहमति से, या किसी घोषित घटना के लिए, ज़्यादा से ज़्यादा चार घंटे, और हर इस्तेमाल ऑडिट लॉग में दर्ज होता है।",
  ),
  securityBreakGlassNone: draft("पिछले 90 दिनों में LAMP सपोर्ट ने आपके बिज़नेस में पहुँच नहीं खोली।"),
  securityBreakGlassWhen: draft("कब"),
  securityBreakGlassWhat: draft("क्या हुआ"),
  securityBreakGlassWhy: draft("क्यों"),
  securityBreakGlassBasis: draft(
    (basis: string) =>
      ({ owner_consent: "किसी मालिक की सहमति से", declared_incident: "घोषित घटना के लिए" })[basis] ?? "",
  ),
  securityBreakGlassUntil: draft((until: string) => `${until} तक`),

  helpLoading: draft("मदद लोड हो रही है…"),
  helpSearch: draft("मदद में खोजें"),
  helpSearchHint: draft("जो जानना है उसके शब्द, जैसे बिल या डिलीवरी"),
  helpMatched: draft((shown: number, matched: number) =>
    shown < matched ? `${matched} में से ${shown} विषय। कम करने के लिए और शब्द जोड़ें।` : `${matched} विषय`,
  ),
  helpNone: draft("किसी विषय में ये सब शब्द नहीं हैं। कम शब्द आज़माएँ, या LAMP से पूछें।"),
  helpAsk: draft("LAMP से पूछें"),
  helpSupport: draft("सपोर्ट तक कैसे पहुँचें"),
  helpSupportAgent: draft(
    'चैट, टेलीग्राम या व्हाट्सऐप पर "help" या "मदद" कहें, या अपना सवाल पूछें। LAMP का सपोर्ट एजेंट किसी भी समय, अंग्रेज़ी, हिंदी या हिंग्लिश में जवाब देता है, उतना जितना आपके रोल आपको देखने देते हैं।',
  ),
  helpSupportPeople: draft(
    "जब वह कुछ सुलझा न पाए, या आपकी कोई शिकायत हो, तो LAMP का कोई व्यक्ति उसे संभालता है: सोमवार से शनिवार, सुबह 10 से शाम 7 बजे तक, अंग्रेज़ी या हिंदी में। वे एक कामकाजी दिन में जवाब देते हैं, हर प्लान पर।",
  ),
  helpGrievance: draft("आपके निजी डेटा के इस्तेमाल की शिकायत LAMP के शिकायत अधिकारी को जाती है:"),
  helpGrievanceLink: draft("संपर्क और शिकायतें"),

  statusLoading: draft("LAMP की जाँच हो रही है…"),
  statusAllWorking: draft("LAMP काम कर रहा है।"),
  statusSomeSlow: draft("LAMP काम कर रहा है, पर कुछ जगह धीमा है।"),
  statusSomeDown: draft("LAMP का एक हिस्सा काम नहीं कर रहा। LAMP की टीम को तुरंत बताया जाता है।"),
  statusPart: draft("हिस्सा"),
  statusState: draft("अभी"),
  statusSince: draft("कब से"),
  statusPartName: draft(
    (part: string) =>
      ({
        api: "कॉकपिट और वेब चैट",
        worker: "पीछे का काम: रन, संदेश और दस्तावेज़",
        models: "AI मॉडल",
        backup: "आखिरी बैकअप",
      })[part] ?? part,
  ),
  statusChannelName: draft(
    (channel: string) =>
      ({ web_chat: "वेब चैट", telegram: "टेलीग्राम", whatsapp: "व्हाट्सऐप", email: "ईमेल" })[channel] ?? channel,
  ),
  statusChannels: draft("चैनल"),
  statusStateName: draft((state: string) => ({ working: "काम कर रहा है", slow: "धीमा", down: "बंद" })[state] ?? state),
  statusBackupAt: draft((when: string) => `${when} को बैकअप हुआ`),
  statusNoBackup: draft("यह LAMP यहाँ दिखाने के लिए कोई बैकअप रिपोर्ट नहीं रखता।"),
  statusCheckedAt: draft((when: string) => `${when} पर जाँचा।`),
  statusCheckAgain: draft("फिर से जाँचें"),

  stopNoCommittedDate: draft(
    ({ order }: StopParameters["no_committed_date"]) =>
      `सेल्स ऑर्डर ${order} की कोई डिलीवरी तारीख़ नहीं है, इसलिए इसके वर्क ऑर्डर की योजना नहीं बन सकती। ` +
      `ग्राहक से वादा की गई तारीख़ बताएँ: “delivery date for ${order} is 24 October” कहें, या इसे इस कार्ड पर दें।`,
  ),
  stopNoBillOfMaterials: draft(
    ({ order, items }: StopParameters["no_bill_of_materials"]) =>
      `${items.join(", ")} का अभी कोई बिल ऑफ़ मटीरियल्स नहीं है, इसलिए सेल्स ऑर्डर ${order} का मटीरियल नहीं जाँचा जा ` +
      `सकता। इसके वर्क ऑर्डर की योजना बन गई है और BOM बनते ही ये आगे बढ़ेंगे।`,
  ),
  stopMaterialShort: draft(
    ({ order, due, lines }: StopParameters["material_short"]) =>
      `सेल्स ऑर्डर ${order} के लिए मटीरियल कम है, डिलीवरी ${dayIn("hi-IN", due)} को। ${lines.map(shortLine).join("। ")}। ` +
      "वर्क ऑर्डर इसके आने तक रिलीज़ होने का इंतज़ार करते हैं।",
  ),
  stopNoGstRate: draft(
    ({ item, hsn, invoice, stale }: StopParameters["no_gst_rate"]) =>
      `${item} (HSN ${hsn}) की कोई GST दर रिकॉर्ड में नहीं है।` +
      `${stale === undefined ? "" : ` आइटम पर लिखी ${stale}% अब GST दर नहीं है।`} ` +
      `${invoice ? `इनवॉइस ${invoice}` : "इनवॉइस"} बनाने के लिए इसकी दर इस कार्ड पर लिखें, जैसे 18%।`,
  ),
  stopAlreadyBilled: draft(
    ({ order, invoices }: StopParameters["already_billed"]) =>
      `सेल्स ऑर्डर ${order} का बिल पहले ही इनवॉइस ${invoices.join(", ")} से बन चुका है: ग्राहक को कोई और इनवॉइस नहीं जाएगा`,
  ),

  statusLampInvoices: draft("LAMP के अपने टैक्स इनवॉइस"),
  statusLampInvoicesReady: draft("LAMP को किए गए हर भुगतान का टैक्स इनवॉइस LAMP बना सकता है।"),
  statusLampInvoicesNotReady: draft(
    "LAMP अभी कोई टैक्स इनवॉइस नहीं बना सकता: सेटिंग्स में LAMP के अपने सप्लायर विवरण (नाम, पता, GSTIN और " +
      "हस्ताक्षरकर्ता) दें। उसके बाद अगली दैनिक जाँच में हर रुका हुआ भुगतान इनवॉइस हो जाएगा।",
  ),
  statusLampInvoicesNoneWaiting: draft("कोई भुगतान अपने टैक्स इनवॉइस की राह नहीं देख रहा।"),
  statusLampInvoicesTenant: draft("कारोबार"),
  statusLampInvoicesPayment: draft("भुगतान"),
  statusLampInvoicesWhy: draft("टैक्स इनवॉइस क्यों नहीं बना"),
  statusLampInvoiceReason: draft(
    (reason: string, said: string) =>
      ({
        own_particulars_missing: "LAMP के अपने सप्लायर विवरण नहीं दिए गए हैं।",
        particulars_missing: "कारोबार ने बिल के लिए इनवॉइस विवरण नहीं चुने हैं।",
        failed: "LAMP इस बार इसे नहीं बना सका; फिर से कोशिश होगी।",
        waiting: "अभी कोशिश नहीं हुई; अगली दैनिक जाँच इसे बनाएगी।",
      })[reason] ?? said,
  ),
};
